// NewOrderPage.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Grid,
  Paper,
  IconButton,
  Tooltip,
  Autocomplete,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  InputLabel,
  Select,
  List,
  ListItem,
  ListItemText,
  Collapse,
  Chip,
  Checkbox,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import heLocale from 'date-fns/locale/he';
import { useNavigate, useLocation } from 'react-router-dom';
import API from '../services/api';
import { heIL } from '@mui/x-data-grid/locales';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useUser } from '../contexts/UserContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
// Import ClientCreateDialog component
import ClientCreateDialog from './dialogs/ClientCreateDialog';

// Icons
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeIcon from '@mui/icons-material/Home';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import SaveIcon from '@mui/icons-material/Save';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PersonIcon from '@mui/icons-material/Person';
import PaymentIcon from '@mui/icons-material/Payment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import PercentIcon from '@mui/icons-material/Percent';
import ClearIcon from '@mui/icons-material/Clear';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ReceiptIcon from '@mui/icons-material/Receipt';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import WarningIcon from '@mui/icons-material/Warning';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Link from '@mui/material/Link';
import MobileDialog from './dialogs/MobileDialog';

// Theme setup
const theme = createTheme({
  direction: 'rtl',
  ...heIL,
});

// Fetch clients function with server-side search
const fetchClients = async (searchTerm = '') => {
  try {
    const params = {
      light: true,
      limit: 100  // Always show 100 clients for consistency
    };

    if (searchTerm) {
      params.search = searchTerm;
    }

    const response = await API.get('/clients/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching clients:', error);
    throw error;
  }
};

// Fetch products function
const fetchProducts = async () => {
  try {
    const response = await API.get('/products/');
    return response.data;
  } catch (error) {
    console.error('Error fetching products:', error);
    throw error;
  }
};

// Draft order deserialization - converts date ISO strings back to Date objects
const DRAFT_TTL_MS = 18 * 60 * 60 * 1000; // 18 hours

const deserializeDraft = (raw) => {
  const draft = JSON.parse(raw);
  // Reconvert formData dates
  if (draft.formData) {
    ['OrderDate', 'DueDate'].forEach(key => {
      if (draft.formData[key]) draft.formData[key] = new Date(draft.formData[key]);
    });
    // Reconvert dates inside payments array
    draft.formData.payments?.forEach(p => {
      if (p.checkDate) p.checkDate = new Date(p.checkDate);
      if (p.transferDate) p.transferDate = new Date(p.transferDate);
    });
  }
  // Reconvert payment method field dates
  if (draft.paymentMethodFields) {
    ['checkDate', 'transferDate'].forEach(key => {
      if (draft.paymentMethodFields[key]) {
        draft.paymentMethodFields[key] = new Date(draft.paymentMethodFields[key]);
      }
    });
  }
  return draft;
};

const fetchProductDetails = async (productId) => {
  try {
    const response = await API.get('/product/', {
      params: { ProductID: productId }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching product details:', error);
    throw error;  
  }
};


// Custom connector that has no visible line
const NoConnector = () => <StepConnector sx={{ display: 'none' }} />;

const NewOrderPageLegacy = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { userProfile } = useUser();
  const location = useLocation(); // Get location object

  const queryParams = new URLSearchParams(location.search);
  const preselectedClientId = queryParams.get('clientId');

  // User-specific storage keys for state persistence (prevents data leakage between users)
  const userId = userProfile?.username || 'unknown';
  const TAX_STATE_KEY = `orderTaxState-${userId}`;
  const ROUNDING_ENABLED_KEY = `orderRoundingEnabled-${userId}`;
  const DRAFT_KEY = `orderDraft-${userId}`;

  // UI config
  const uiConfig = userProfile?.ui_config || {};
  const orderFields = uiConfig.order_fields || []; 
  const orderFieldsSchema = uiConfig.order_fields_schema || {};
  const generalSchema = uiConfig?.orders_general_configs?.general_schema || {}; 
  const orderItemFields = uiConfig.order_item_fields || [];
  const orderItemFieldsSchema = uiConfig.order_item_fields_schema || {};
  const productLabel = generalSchema?.product?.label || 'מוצר';
  const productPluralLabel = generalSchema?.product?.plural_label || 'מוצרים';
  const orderLabel = generalSchema?.order?.label || 'הזמנה';
  const orderPluralLabel = generalSchema?.order?.plural_label || 'הזמנות';
  const newOrderLabel = generalSchema?.new_order?.label || 'הזמנה חדשה';
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Stepper state
  const [activeStep, setActiveStep] = useState(0);
  const steps = ['בחירת לקוח', `הוספת ${productPluralLabel}`, 'פרטי תשלום', 'סיכום'];
  
  const allowOrderManagement = uiConfig.allow_order_management || false;
  const orderConfig = userProfile?.group?.order_config || {};
  const storageConfig = orderConfig.storage_config || {};
  const storageEnabled = storageConfig.enabled && allowOrderManagement;
  const productFields = uiConfig.product_fields || [];
  const productFieldsSchema = uiConfig.product_fields_schema || {};
  const [selectedStorage, setSelectedStorage] = useState(null);
  const [availableStorages, setAvailableStorages] = useState([]);
  // Form state
  const [formData, setFormData] = useState({
    ClientBusinessID: '',
    ClientName: '',
    OrderDate: new Date(),
    DueDate: null,
    Status: 'pending',
    TotalAmount: 0,
    Subtotal: 0,
    DiscountAmount: '',
    DiscountPercentage: '',
    DiscountReason: '',
    PaidAmount: 0,
    InvoiceNumber: '',
    Comments: '',
    payments: [],
    // Tax-related fields
    IsTaxable: false,
    IsTaxIncluded: false,
    TaxRate: 18.00,
    TaxAmount: 0,
    NetAmount: 0,
  });

  // State for client creation dialog
  const [createClientDialogOpen, setCreateClientDialogOpen] = useState(false);

  // Discount state
  const [discountType, setDiscountType] = useState('none'); // none, amount, percentage

  // Tax state
  const [taxState, setTaxState] = useState('none'); // none, included, excluded

  // Rounding state
  const [roundingEnabled, setRoundingEnabled] = useState(false);

  const [productEntries, setProductEntries] = useState([]);

  
  // UI state
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productQuantity, setProductQuantity] = useState('');
  const [productUnitPrice, setProductUnitPrice] = useState('');
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [addPaymentOpen, setAddPaymentOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethodFields, setPaymentMethodFields] = useState({
    checkNumber: '',
    checkDate: null,
    checkBankName: '',
    checkAccountNumber: '',
    cardLastFour: '',
    cardType: '',
    bankName: '',
    bankAccountLast4: '',
    transferDate: null
  });
  const [editingProductId, setEditingProductId] = useState(null);

  // Refs for product dialog inputs
  const productSearchInputRef = useRef(null);
  const quantityInputRef = useRef(null);
  const unitPriceInputRef = useRef(null);
  const addProductButtonRef = useRef(null);
  const isFinishingProductsRef = useRef(false);
  const isAddingProductRef = useRef(false);

  const [newProductInventory, setNewProductInventory] = useState({});
  const [creatingProductWithInventory, setCreatingProductWithInventory] = useState(false);
  const [selectedStorageForNewProduct, setSelectedStorageForNewProduct] = useState(null);

  // New payment state
  const [editingPaymentIndex, setEditingPaymentIndex] = useState(null);
  const [remainingBalance, setRemainingBalance] = useState(0);
  
  // New state for enhanced product dialog
  const [productCreationMode, setProductCreationMode] = useState(false);
  const [addedProducts, setAddedProducts] = useState([]);
  const [createProductError, setCreateProductError] = useState('');
  const [createProductSuccess, setCreateProductSuccess] = useState('');
  const [noInventoryWarning, setNoInventoryWarning] = useState(false);
  const [isFinishingProducts, setIsFinishingProducts] = useState(false);
  const [isAddingProduct, setIsAddingProduct] = useState(false);
  const [productAutocompleteOpen, setProductAutocompleteOpen] = useState(false);
  const [clientAutocompleteOpen, setClientAutocompleteOpen] = useState(true);

  // Draft restore state
  const [draftDialogOpen, setDraftDialogOpen] = useState(false);
  const [pendingDraft, setPendingDraft] = useState(null);

  // Debounced autocomplete search state
  const [debouncedAutocompleteSearch, setDebouncedAutocompleteSearch] = useState('');
  const autocompleteTimerCleanup = useRef(null);

  const excludedFields = ['CreatedAt', 'IsDeleted', 'DeletedAt', 'ProductID', 'UserGroup'];
  const [newProductData, setNewProductData] = useState(() => {
    const initialData = {
      ProductName: '',
      Description: '',
      Price: 0,
      Quantity: ''
    };
    
    // Add all non-readonly and non-excluded fields from schema
    Object.entries(productFieldsSchema).forEach(([fieldName, fieldConfig]) => {
      if (!fieldConfig.readonly && 
          !initialData.hasOwnProperty(fieldName) && 
          productFields.includes(fieldName) &&
          !excludedFields.includes(fieldName)) {
        initialData[fieldName] = fieldConfig.default || '';
      }
    });
    
    return initialData;
  });
    

  // Add helper function to render fields dynamically
  const renderProductField = (fieldName, fieldConfig) => {
    // Skip readonly fields and excluded fields
    if (fieldConfig.readonly || excludedFields.includes(fieldName)) {
      return null;
    }
    
    const fieldType = fieldConfig.type || 'string';
    const label = fieldConfig.label || fieldName;
    const required = fieldConfig.required || false;
    
    switch (fieldType) {
      case 'number':
        return (
          <Grid item xs={12} sm={6} key={fieldName}>
            <TextField
              name={fieldName}
              label={label}
              fullWidth
              value={newProductData[fieldName] || ''}
              onChange={handleNewProductChange}
              type="number"
              required={required}
              variant="outlined"
              InputProps={{
                inputProps: { 
                  min: fieldConfig.min || 0, 
                  max: fieldConfig.max || undefined,
                  step: 0.01 
                }
              }}
            />
          </Grid>
        );
      
      case 'text':
        return (
          <Grid item xs={12} key={fieldName}>
            <TextField
              name={fieldName}
              label={label}
              fullWidth
              value={newProductData[fieldName] || ''}
              onChange={handleNewProductChange}
              multiline
              rows={2}
              required={required}
              variant="outlined"
            />
          </Grid>
        );
        
      case 'boolean':
        return (
          <Grid item xs={12} sm={6} key={fieldName}>
            <FormControlLabel
              control={
                <Switch
                  checked={newProductData[fieldName] || false}
                  onChange={(e) => setNewProductData(prev => ({
                    ...prev,
                    [fieldName]: e.target.checked
                  }))}
                  name={fieldName}
                />
              }
              label={label}
            />
          </Grid>
        );
        
      case 'select':
        return (
          <Grid item xs={12} sm={6} key={fieldName}>
            <TextField
              name={fieldName}
              label={label}
              fullWidth
              value={newProductData[fieldName] || ''}
              onChange={handleNewProductChange}
              select
              required={required}
              variant="outlined"
            >
              <MenuItem value="">
                <em>בחר</em>
              </MenuItem>
              {fieldConfig.options?.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        );
        
      case 'date':
        return (
          <Grid item xs={12} sm={6} key={fieldName}>
            <TextField
              name={fieldName}
              label={label}
              fullWidth
              type="date"
              value={newProductData[fieldName] || ''}
              onChange={handleNewProductChange}
              required={required}
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
        );
        
      default: // string
        return (
          <Grid item xs={12} sm={6} key={fieldName}>
            <TextField
              name={fieldName}
              label={label}
              fullWidth
              value={newProductData[fieldName] || ''}
              onChange={handleNewProductChange}
              required={required}
              variant="outlined"
            />
          </Grid>
        );
    }
  };


  // Add memoized filter function for products
  const filterProductsEnhanced = useCallback((options, params) => {
    const { inputValue } = params;
    const input = inputValue.toLowerCase().trim();
    
    if (!input) {
      return options.slice(0, 20);
    }
    
    if (input.length < 2) {
      return [];
    }
    
    // Check if barcode and catalog number are in the UI config
    const showBarcode = productFields.includes('Barcode');
    const showCatalogNumber = productFields.includes('CatalogNumber');
    const showEnhancedSearch = showBarcode || showCatalogNumber || storageEnabled;
    
    const filtered = options.map(product => {
      const productName = (product.ProductName || '').toLowerCase();
      const productId = (product.ProductID || '').toString();
      
      // Basic search (always active)
      let matches = productName.includes(input) || productId.includes(input);
      let matchedField = null;
      
      // Enhanced search (only if fields are in UI config or storage enabled)
      if (showEnhancedSearch) {
        const barcode = showBarcode ? (product.Barcode || '').toLowerCase() : '';
        const catalogNumber = showCatalogNumber ? (product.CatalogNumber || '').toLowerCase() : '';
        
        // Check which field matches
        if (productName.includes(input)) {
          matchedField = productFieldsSchema.ProductName?.label || 'שם מוצר';
        } else if (showCatalogNumber && catalogNumber.includes(input)) {
          matchedField = productFieldsSchema.CatalogNumber?.label || 'מק"ט';
        } else if (showBarcode && barcode.includes(input)) {
          matchedField = productFieldsSchema.Barcode?.label || 'ברקוד';
        } else if (productId.includes(input)) {
          matchedField = 'מזהה';
        }
        
        matches = matches || 
                  (showBarcode && barcode.includes(input)) || 
                  (showCatalogNumber && catalogNumber.includes(input));
      }
      
      if (matches) {
        return showEnhancedSearch ? { ...product, matchedField } : product;
      }
      return null;
    }).filter(Boolean);
    
    return filtered.slice(0, 50);
  }, [productFields, productFieldsSchema, storageEnabled]);
  
  const getProductFieldsInOrder = () => {
    const orderedFields = [];
    
    // Special order fields that have specific positions
    const specialOrderFields = [
      'ProductName',
      'CatalogNumber', 
      'Barcode',
      'Price',
      'Unit',
      'Description'
    ];
    
    // 1. Always show ProductName first
    orderedFields.push('ProductName');
    
    // 2. Add CatalogNumber if in product_fields
    if (productFields.includes('CatalogNumber') && 
        !excludedFields.includes('CatalogNumber') &&
        (!productFieldsSchema.CatalogNumber || !productFieldsSchema.CatalogNumber.readonly)) {
      orderedFields.push('CatalogNumber');
    }
    
    // 3. Add Barcode if in product_fields
    if (productFields.includes('Barcode') && 
        !excludedFields.includes('Barcode') &&
        (!productFieldsSchema.Barcode || !productFieldsSchema.Barcode.readonly)) {
      orderedFields.push('Barcode');
    }
    
    // 4. Always show Price
    orderedFields.push('Price');
    
    // 5. Add Unit if in product_fields
    if (productFields.includes('Unit') && 
        !excludedFields.includes('Unit') &&
        (!productFieldsSchema.Unit || !productFieldsSchema.Unit.readonly)) {
      orderedFields.push('Unit');
    }
    
    // 6. Add all other fields from product_fields (excluding already added and Description)
    productFields.forEach(fieldName => {
      if (!orderedFields.includes(fieldName) && 
          !excludedFields.includes(fieldName) &&
          !specialOrderFields.includes(fieldName) &&
          productFieldsSchema[fieldName] &&
          !productFieldsSchema[fieldName].readonly) {
        orderedFields.push(fieldName);
      }
    });
    
    // 7. Add Description last
    if ((productFields.includes('Description') || productFields.length === 0) && 
        !excludedFields.includes('Description') &&
        (!productFieldsSchema.Description || !productFieldsSchema.Description.readonly)) {
      orderedFields.push('Description');
    }
    
    return orderedFields;
  };

  // Update reset function to include all fields
  const resetNewProductData = () => {
    const resetData = {
      ProductName: '',
      Description: '',
      Price: '0',
      Quantity: 1
    };
    
    Object.entries(productFieldsSchema).forEach(([fieldName, fieldConfig]) => {
      if (!fieldConfig.readonly && 
          !resetData.hasOwnProperty(fieldName) && 
          productFields.includes(fieldName) &&
          !excludedFields.includes(fieldName)) {
        resetData[fieldName] = fieldConfig.default || '';
      }
    });
    
    setNewProductData(resetData);
  };

  // Debounced callback for autocomplete search
  const debouncedSetAutocompleteSearch = useCallback((value) => {
    const timer = setTimeout(() => {
      setDebouncedAutocompleteSearch(value);
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  // React Query - clients with server-side search
  const {
    data: clients = [],
    isLoading: isLoadingClients,
    isError: isErrorClients,
    error: clientsError,
  } = useQuery({
    queryKey: ['clients', 'autocomplete', debouncedAutocompleteSearch],
    queryFn: () => fetchClients(debouncedAutocompleteSearch),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Create autocomplete options with dummy "no results" record when empty (and user is searching)
  const clientAutocompleteOptions = React.useMemo(() => {
    if (clients.length === 0 && debouncedAutocompleteSearch) {
      return [{ ClientBusinessID: -1, ClientName: 'אין תוצאות', isNoResults: true }];
    }
    return clients;
  }, [clients, debouncedAutocompleteSearch]);

  const {
    data: products = [],
    isLoading: isLoadingProducts,
    isError: isErrorProducts,
    error: productsError,
  } = useQuery({
    queryKey: ['products'],
    queryFn: fetchProducts,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
  
  const {
    data: allStorages = [],
    isLoading: isLoadingStorages,
  } = useQuery({
    queryKey: ['storages'],
    queryFn: async () => {
      const response = await API.get('/storages/');
      return response.data;
    },
    enabled: storageEnabled,
    staleTime: 5 * 60 * 1000,
  });

  // Create order mutation
  const createOrderMutation = useMutation({
    mutationFn: async (orderData) => {
      const response = await API.post('/order/create/', orderData);
      return response.data;
    },
    onSuccess: (data) => {
      setFormSuccess(`${orderLabel} נוצרה בהצלחה`);
      queryClient.invalidateQueries(['orders']);
      localStorage.removeItem(DRAFT_KEY);
      // DON'T reset isSubmitting here - keep it true until navigation
      
      // Navigate to the order detail page
      setTimeout(() => {
        navigate(`/order/${data.OrderID}`, { state: { from: '/orders' } });
      }, 1500);
    },
    onError: (error) => {
      setFormError(error?.response?.data?.error || `שגיאה ביצירת ${orderLabel}`);
      setActiveStep(3); // Move to summary step to show error
      setIsSubmitting(false); // Only reset on error
    },
  }); 
  
  // New create product mutation
  const createProductMutation = useMutation({
    mutationFn: async (productData) => {
      setCreatingProductWithInventory(true);
      
      try {
        // Step 1: Create the product
        const productResponse = await API.post('/products/create/', productData.product);
        const newProduct = productResponse.data;
        
        // Step 2: If storage enabled and inventory data provided, create inventory records
        if (storageEnabled && productData.inventory) {
          const inventoryPromises = [];
          
          for (const [storageId, data] of Object.entries(productData.inventory)) {
            if (data.quantity > 0) {
              inventoryPromises.push(
                API.post('/inventory/create/', {
                  ProductID: newProduct.ProductID,
                  StorageID: parseInt(storageId),
                  available_quantity: data.quantity
                })
              );
            }
          }
          
          if (inventoryPromises.length > 0) {
            await Promise.all(inventoryPromises);
          }
        }
        
        return { 
          product: newProduct, 
          selectedStorage: productData.selectedStorage 
        };
      } finally {
        setCreatingProductWithInventory(false);
      }
    },
    onSuccess: (data) => {
      setCreateProductSuccess(`${productLabel} נוצר בהצלחה`);
      
      // Refresh products list
      queryClient.invalidateQueries(['products']);
      
      // Create enhanced product object with inventory info
      const enhancedProduct = {
        ...data.product,
        inventory: storageEnabled ? Object.entries(newProductInventory)
          .filter(([_, data]) => data.quantity > 0)
          .map(([storageId, data]) => ({
            StorageID: parseInt(storageId),
            StorageName: data.storageName,
            available_quantity: data.quantity,
            is_default: data.is_default
          })) : []
      };
      
      // Create product entry with selected storage
      const newProductEntry = {
        product: enhancedProduct,
        quantity: Math.round(parseFloat(newProductData.Quantity) * 100) / 100 || 1,
        customPrice: null,
        entryId: Date.now(),
        storageId: data.selectedStorage?.StorageID || null,
        storageName: data.selectedStorage?.StorageName || null
      };
      
      // Add to the added products list
      setAddedProducts(prev => [...prev, newProductEntry]);
      
      // Reset form
      setNewProductData({
        ProductName: '',
        Description: '',
        Price: '',
        Quantity: ''
      });
      setNewProductInventory({});
      setSelectedStorageForNewProduct(null);
      
      // Exit creation mode
      setProductCreationMode(false);
      
      setTimeout(() => {
        setCreateProductSuccess('');
      }, 1500);
    },
    onError: (error) => {
      setCreateProductError(error?.response?.data?.error || `שגיאה ביצירת ${productLabel}`);
      setCreatingProductWithInventory(false);
    },
  });
    
  // Client dialog handlers
  const handleOpenCreateClientDialog = () => {
    setCreateClientDialogOpen(true);
  };

  const handleCloseCreateClientDialog = () => {
    setCreateClientDialogOpen(false);

    // Refetch the clients list to get the newly created client
    queryClient.refetchQueries(['clients', 'autocomplete']);
  };

  const formatCurrency = (num) =>
    new Intl.NumberFormat('he-IL', { style:'currency', currency:'ILS' }).format(num);
  
  // Calculate the total paid amount whenever payments change
  useEffect(() => {
    const totalPaid = formData.payments.reduce((sum, payment) => sum + parseFloat(payment.amount || 0), 0);
    setFormData(prev => ({
      ...prev,
      PaidAmount: totalPaid,
    }));
  }, [formData.payments]);
  
  // Calculate remaining balance whenever total or paid amount changes
  useEffect(() => {
    const balance = formData.TotalAmount - formData.PaidAmount;
    setRemainingBalance(balance);
  }, [formData.TotalAmount, formData.PaidAmount]);
  
  const renderPriceCell = (originalRaw, overriddenRaw) => {
    // Parse strings or numbers into floats
    const original = parseFloat(originalRaw);
    const overridden = overriddenRaw != null ? parseFloat(overriddenRaw) : null;
    
    const isOver = overridden != null && overridden !== original;
    const chosen = isOver ? overridden : original;
    return (
      <Box sx={{ display:'flex', alignItems:'right' }}>
        {isOver && (
          <Typography sx={{ textDecoration:'line-through', mr:1 }}>
            {formatCurrency(original)}
          </Typography>
        )}
        <Typography sx={{marginRight: 2 }}>{formatCurrency(chosen)}</Typography>
      </Box>
    );
  };
  
  // Update subtotal and total amounts when products change
  useEffect(() => {
    // Calculate subtotal
    const subtotal = productEntries.reduce((sum, entry) => {
      const price = parseFloat(entry.customPrice || entry.product.Price) || 0;
      const quantity = parseFloat(entry.quantity) || 0;
      return sum + (price * quantity);
    }, 0);

    // Calculate discount amount with proper rounding
    let discountAmount = 0;
    if (discountType === 'amount') {
      // Round discount amount to 2 decimal places
      discountAmount = Math.round((parseFloat(formData.DiscountAmount) || 0) * 100) / 100;
    } else if (discountType === 'percentage') {
      // Round percentage to 2 decimal places, then calculate discount
      const percentage = Math.round((parseFloat(formData.DiscountPercentage) || 0) * 100) / 100;
      discountAmount = Math.round((subtotal * (percentage / 100)) * 100) / 100;
    }

    // Amount after discount
    const amountAfterDiscount = Math.round((subtotal - discountAmount) * 100) / 100;

    // Calculate tax based on tax state
    let totalAmount, taxAmount, netAmount;
    const taxRate = formData.TaxRate || 18.00;

    if (taxState === 'none') {
      // No tax
      netAmount = amountAfterDiscount;
      taxAmount = 0;
      totalAmount = amountAfterDiscount;
    } else if (taxState === 'included') {
      // Prices include tax - extract it
      totalAmount = amountAfterDiscount;
      netAmount = Math.round((amountAfterDiscount / (1 + taxRate / 100)) * 100) / 100;
      taxAmount = Math.round((totalAmount - netAmount) * 100) / 100;
    } else if (taxState === 'excluded') {
      // Prices exclude tax - add it
      netAmount = amountAfterDiscount;
      taxAmount = Math.round((amountAfterDiscount * (taxRate / 100)) * 100) / 100;
      totalAmount = Math.round((netAmount + taxAmount) * 100) / 100;
    }

    // Apply rounding if enabled (ALWAYS DOWN)
    let finalTotal = totalAmount;
    let roundingAdjustment = 0;

    if (roundingEnabled) {
      const rounded = Math.floor(totalAmount); // Floor to integer
      roundingAdjustment = rounded - totalAmount; // Will be negative or zero
      finalTotal = rounded;
    }

    setFormData(prev => ({
      ...prev,
      Subtotal: Math.round(subtotal * 100) / 100,
      DiscountAmount: discountAmount, // Store the calculated discount amount
      NetAmount: netAmount,
      TaxAmount: taxAmount,
      TotalAmount: finalTotal,
      RoundingAdjustment: roundingAdjustment,
    }));
  }, [productEntries, formData.DiscountAmount, formData.DiscountPercentage, discountType, taxState, formData.TaxRate, roundingEnabled]);
  
  // Initialize product unit price when selected product changes
  useEffect(() => {
    if (selectedProduct && !productCreationMode) {
      // Only set price here if it's not already set
      if (!productUnitPrice && selectedProduct.Price) {
        setProductUnitPrice(selectedProduct.Price.toString());
      }
    } else if (!selectedProduct) {
      setProductUnitPrice('');
    }
  }, [selectedProduct, productCreationMode]);

  // Reset payment fields when payment method changes
  useEffect(() => {
    setPaymentMethodFields({
      checkNumber: '',
      checkDate: null,
      checkBankName: '',
      checkAccountNumber: '',
      cardLastFour: '',
      cardType: '',
      bankName: '',
      bankAccountLast4: '',
      transferDate: null
    });
  }, [paymentMethod]);
  
  // Event handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };
  
  // Draft detection on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;

      const draft = deserializeDraft(raw);

      // Check TTL
      if (Date.now() - draft.savedAt > DRAFT_TTL_MS) {
        localStorage.removeItem(DRAFT_KEY);
        return;
      }

      setPendingDraft(draft);
      setDraftDialogOpen(true);
    } catch (e) {
      console.warn('Failed to load order draft:', e);
      localStorage.removeItem(DRAFT_KEY);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Draft restore/discard handlers
  const handleRestoreDraft = useCallback(() => {
    if (!pendingDraft) return;

    setFormData(pendingDraft.formData);
    setSelectedClient(pendingDraft.selectedClient);
    setProductEntries(pendingDraft.productEntries || []);
    setDiscountType(pendingDraft.discountType || 'none');
    setTaxState(pendingDraft.taxState || 'none');
    setRoundingEnabled(pendingDraft.roundingEnabled || false);
    setActiveStep(pendingDraft.activeStep || 0);
    setPaymentMethod(pendingDraft.paymentMethod || 'cash');
    setPaymentAmount(pendingDraft.paymentAmount || '');
    setPaymentMethodFields(pendingDraft.paymentMethodFields || {
      checkNumber: '', checkDate: null, checkBankName: '', checkAccountNumber: '',
      cardLastFour: '', cardType: '', bankName: '', bankAccountLast4: '', transferDate: null
    });

    setDraftDialogOpen(false);
    setPendingDraft(null);
  }, [pendingDraft]);

  const handleDiscardDraft = useCallback(() => {
    localStorage.removeItem(DRAFT_KEY);
    setDraftDialogOpen(false);
    setPendingDraft(null);
  }, [DRAFT_KEY]);

  useEffect(() => {
    if (preselectedClientId && clients.length > 0 && !selectedClient) {
      const clientToSelect = clients.find(client =>
        client.ClientBusinessID === preselectedClientId
      );

      if (clientToSelect) {
        // Set the selected client and update form data
        setSelectedClient(clientToSelect);
        setFormData(prev => ({
          ...prev,
          ClientBusinessID: clientToSelect.ClientBusinessID,
          ClientName: clientToSelect.ClientName,
        }));

        // Optionally, if a client is preselected, automatically move to the next step
        // This is useful to skip the client selection step since we already have the client
        setActiveStep(1); // Move to product selection step
      }
    }
  }, [preselectedClientId, clients, selectedClient]);

  // Load tax state from localStorage on component mount
  useEffect(() => {
    const savedTaxState = localStorage.getItem(TAX_STATE_KEY);
    if (savedTaxState && ['none', 'included', 'excluded'].includes(savedTaxState)) {
      setTaxState(savedTaxState);

      // Update formData based on loaded tax state
      if (savedTaxState === 'none') {
        setFormData(prev => ({
          ...prev,
          IsTaxable: false,
          IsTaxIncluded: false,
        }));
      } else if (savedTaxState === 'included') {
        setFormData(prev => ({
          ...prev,
          IsTaxable: true,
          IsTaxIncluded: true,
        }));
      } else if (savedTaxState === 'excluded') {
        setFormData(prev => ({
          ...prev,
          IsTaxable: true,
          IsTaxIncluded: false,
        }));
      }
    }
  }, []); // Run only once on mount

  // Load rounding state from localStorage on component mount
  useEffect(() => {
    const savedRounding = localStorage.getItem(ROUNDING_ENABLED_KEY);
    if (savedRounding) {
      setRoundingEnabled(savedRounding === 'true');
    }
  }, []); // Run only once on mount

  // Save draft to localStorage (debounced)
  useEffect(() => {
    // Don't save if form was successfully submitted or is submitting
    if (formSuccess || isSubmitting) return;
    // Don't save if form is pristine
    if (!selectedClient && productEntries.length === 0) return;

    const timer = setTimeout(() => {
      try {
        const draft = {
          savedAt: Date.now(),
          formData,
          selectedClient,
          productEntries,
          discountType,
          taxState,
          roundingEnabled,
          activeStep,
          paymentMethod,
          paymentAmount,
          paymentMethodFields,
        };
        localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
      } catch (e) {
        console.warn('Failed to save order draft:', e);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [formSuccess, isSubmitting, formData, selectedClient, productEntries, discountType, taxState,
      roundingEnabled, activeStep, paymentMethod, paymentAmount, paymentMethodFields, DRAFT_KEY]);

  // Initialize inventory data when entering product creation mode
  useEffect(() => {
    if (productCreationMode && storageEnabled && allStorages.length > 0) {
      // Initialize inventory with 0 for all storages
      const initialInventory = {};
      allStorages.forEach(storage => {
        initialInventory[storage.StorageID] = {
          quantity: 0,
          storageName: storage.StorageName,
          is_default: storage.is_default
        };
      });
      setNewProductInventory(initialInventory);
      
      // Pre-select default storage
      const defaultStorage = allStorages.find(s => s.is_default);
      setSelectedStorageForNewProduct(defaultStorage || allStorages[0]);
    }
  }, [productCreationMode, storageEnabled, allStorages]);

  const handleClientSelect = (event, newValue, reason) => {
    // Handle object selection (valid client selected from dropdown)
    if (newValue && typeof newValue === 'object') {
      setSelectedClient(newValue);
      setFormData(prev => ({
        ...prev,
        ClientBusinessID: newValue.ClientBusinessID,
        ClientName: newValue.ClientName,
      }));
    }
    // Handle string input (freeSolo - user typed without selecting)
    else if (typeof newValue === 'string') {
      // Don't accept string values - require valid client selection
      // Keep the typed text visible but don't set as selected client
      return;
    }
    // Handle clearing
    else {
      setSelectedClient(null);
      setFormData(prev => ({
        ...prev,
        ClientBusinessID: '',
        ClientName: '',
      }));

      // Only clear autocomplete search when explicitly clearing (not when typing)
      if (reason === 'clear') {
        setDebouncedAutocompleteSearch('');
        if (autocompleteTimerCleanup.current) {
          autocompleteTimerCleanup.current();
        }
      }
    }
  };
  
  // Update the handleProductSelect function  
    const handleProductSelect = async (event, newValue) => {
    if (newValue) {
      setNoInventoryWarning(false); // Reset warning
      setSelectedProduct(newValue);
      
      if (storageEnabled) {
        try {
          const fullProductData = await fetchProductDetails(newValue.ProductID);
          setSelectedProduct(fullProductData);
          
          setProductUnitPrice(fullProductData.Price.toString());
          
          // Check if product has any inventory
          if (fullProductData.inventory && fullProductData.inventory.length > 0) {
            const storagesWithInventory = fullProductData.inventory.filter(inv => inv.available_quantity > 0);
            
            if (storagesWithInventory.length === 0) {
              // Product has no inventory in any storage
              setNoInventoryWarning(true);
              setAvailableStorages([]);
              setSelectedStorage(null);
            } else {
              setAvailableStorages(storagesWithInventory);
              
              const defaultStorage = storagesWithInventory.find(inv => inv.is_default);
              if (defaultStorage) {
                setSelectedStorage(defaultStorage);
              } else {
                setSelectedStorage(storagesWithInventory[0]);
              }
            }
          } else {
            // No inventory records at all
            setNoInventoryWarning(true);
            setAvailableStorages([]);
            setSelectedStorage(null);
          }
        } catch (error) {
          console.error('Failed to fetch product details:', error);
          setFormError('שגיאה בטעינת פרטי המוצר');
        }
        // Focus quantity field after product selection (delay for autocomplete to close)
        setTimeout(() => {
          quantityInputRef.current?.focus();
          quantityInputRef.current?.select();
        }, 150);
      } else {
        setProductUnitPrice(newValue.Price.toString());
        // Focus quantity field after product selection (delay for autocomplete to close)
        setTimeout(() => {
          quantityInputRef.current?.focus();
          quantityInputRef.current?.select();
        }, 150);
      }
    } else {
      setSelectedProduct(null);
      setProductUnitPrice('');
      setAvailableStorages([]);
      setSelectedStorage(null);
      setNoInventoryWarning(false);
    }
  };

  const openProductDialog = async (index = null) => {
    if (index !== null) {
      // Edit existing product entry
      const entryToEdit = productEntries[index];
      
      // If storage is enabled, fetch full product data
      if (storageEnabled) {
        try {
          const fullProductData = await fetchProductDetails(entryToEdit.product.ProductID);
          setSelectedProduct(fullProductData);
          
          // Set up available storages
          if (fullProductData.inventory && fullProductData.inventory.length > 0) {
            const storagesWithInventory = fullProductData.inventory.filter(inv => inv.available_quantity > 0);
            setAvailableStorages(storagesWithInventory);
            
            // Find the previously selected storage
            if (entryToEdit.storageId) {
              const storage = storagesWithInventory.find(inv => inv.StorageID === entryToEdit.storageId);
              setSelectedStorage(storage || storagesWithInventory[0] || null);
            } else {
              const defaultStorage = storagesWithInventory.find(inv => inv.is_default);
              setSelectedStorage(defaultStorage || storagesWithInventory[0] || null);
            }
          }
        } catch (error) {
          console.error('Failed to fetch product details:', error);
          setFormError('שגיאה בטעינת פרטי המוצר');
          return;
        }
      } else {
        setSelectedProduct(entryToEdit.product);
      }
      
      setProductQuantity(entryToEdit.quantity);
      setProductUnitPrice((entryToEdit.customPrice || entryToEdit.product.Price).toString());
      setEditingProductId(index);
    } else {
      // Add new product - reset everything
      setSelectedProduct(null);
      setProductQuantity('');
      setProductUnitPrice('');
      setEditingProductId(null);
      setAddedProducts([]);
      setSelectedStorage(null);
      setAvailableStorages([]);
    }
    
    setProductDialogOpen(true);
    setProductCreationMode(false);
    setCreateProductError('');
    setCreateProductSuccess('');
    setProductAutocompleteOpen(true);
  };

  
  // Handler to toggle product creation mode
  const toggleProductCreationMode = () => {
    setProductCreationMode(!productCreationMode);
    setCreateProductError('');
    setCreateProductSuccess('');
    setNewProductData({
      ProductName: '',
      Description: '',
      Price: '',
      Quantity: 1
    });
  };
  
  // Handler for new product form fields
  const handleNewProductChange = (e) => {
    const { name, value } = e.target;
    setNewProductData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handler to create a new product
  const handleCreateProduct = () => {
    setCreateProductError('');
    
    // Basic validation
    if (!newProductData.ProductName) {
      setCreateProductError(`נא להזין שם ${productLabel}`);
      return;
    }
    
    const price = newProductData.Price === '' ? 0 : parseFloat(newProductData.Price);
    if (isNaN(price) || price < 0) {
      setCreateProductError('נא להזין מחיר תקין (0 או יותר)');
      return;
    }
    
    if (!newProductData.Quantity || isNaN(parseFloat(newProductData.Quantity)) || parseFloat(newProductData.Quantity) <= 0) {
      setCreateProductError('נא להזין כמות תקינה');
      return;
    }
    
    if (storageEnabled && selectedStorageForNewProduct) {
      const orderQuantity = parseFloat(newProductData.Quantity);
      const storageInventory = newProductInventory[selectedStorageForNewProduct.StorageID]?.quantity || 0;
      
      if (orderQuantity > storageInventory) {
        setCreateProductError(
          `הכמות להזמנה (${orderQuantity}) גדולה מהמלאי שהוגדר למחסן ${selectedStorageForNewProduct.StorageName} (${storageInventory}). ` +
          `נא להגדיל את המלאי במחסן או להקטין את הכמות להזמנה.`
        );
        return;
      }
      
      if (storageInventory === 0) {
        setCreateProductError(
          `לא הוגדר מלאי במחסן ${selectedStorageForNewProduct.StorageName}. ` +
          `נא להגדיר כמות מלאי במחסן הנבחר.`
        );
        return;
      }
    }
    
    // Validate other required fields from schema
    const requiredFields = Object.entries(productFieldsSchema)
      .filter(([fieldName, config]) => 
        config.required && 
        productFields.includes(fieldName) && 
        !excludedFields.includes(fieldName) &&
        fieldName !== 'ProductName' && 
        fieldName !== 'Price'
      );
    
    for (const [fieldName, config] of requiredFields) {
      if (!newProductData[fieldName] || newProductData[fieldName].toString().trim() === '') {
        setCreateProductError(`${config.label || fieldName} הוא שדה חובה`);
        return;
      }
    }
    
    // Rest of validation...
    
    // Prepare data for submission - only send non-empty values
    const productData = {};
    Object.entries(newProductData).forEach(([key, value]) => {
      if (key !== 'Quantity' && value !== '' && value !== null && value !== undefined) {
        if (key === 'Price') {
          productData[key] = Math.round(price * 100) / 100;
        } else {
          productData[key] = value;
        }
      }
    });
    
    const submitData = {
      product: productData,
      inventory: storageEnabled ? newProductInventory : null,
      selectedStorage: storageEnabled ? selectedStorageForNewProduct : null
    };
    
    createProductMutation.mutate(submitData);
  };
  
  // Original handler for adding a single product and closing
  const handleAddProduct = () => {
    if (!selectedProduct) return;
    
    if (storageEnabled && noInventoryWarning) {
      setFormError(`לא ניתן להוסיף ${productLabel} ללא מלאי. יש לעדכן את המלאי תחילה.`);
      return;
    }

    if (!productQuantity || parseFloat(productQuantity) <= 0) {
    setFormError('נא להזין כמות');
    return;
    }

    const price = parseFloat(productUnitPrice) || selectedProduct.Price;
    const isCustomPrice = Math.abs(Number(price) - Number(selectedProduct.Price)) > 0.001;
    
    // Validation for storage if enabled
    if (storageEnabled && selectedStorage) {
        const qty = parseFloat(productQuantity) || 0;
        if (qty > selectedStorage.available_quantity) {
          setFormError(`הכמות המבוקשת (${qty}) עולה על המלאי הזמין (${selectedStorage.available_quantity})`);
          return;
        }
      }
    
    // Check if we're editing an existing product
    if (editingProductId !== null) {
      // Update the specific product entry
      setProductEntries(prev => prev.map((entry, idx) => 
        idx === editingProductId ? {
          ...entry,
          quantity: Math.round(productQuantity * 100) / 100,
          customPrice: isCustomPrice ? price : null,
          storageId: selectedStorage?.StorageID || null,
          storageName: selectedStorage?.StorageName || null
        } : entry
      ));
    } else {
      // Check if the same product with same price AND same storage already exists
      const existingEntryIndex = productEntries.findIndex(entry => {
        const sameProduct = entry.product.ProductID === selectedProduct.ProductID;
        const samePrice = ((!entry.customPrice && !isCustomPrice) || 
                          (entry.customPrice && isCustomPrice && Math.abs(Number(entry.customPrice) - Number(price)) < 0.001));
        const sameStorage = storageEnabled 
          ? entry.storageId === (selectedStorage?.StorageID || null)
          : true; // If storage not enabled, ignore storage in comparison
        
        return sameProduct && samePrice && sameStorage;
      });
      
      if (existingEntryIndex >= 0) {
        // Update quantity of existing entry
        const updatedEntries = [...productEntries];
        updatedEntries[existingEntryIndex].quantity = Math.round((parseFloat(updatedEntries[existingEntryIndex].quantity) + parseFloat(productQuantity)) * 100) / 100;
        setProductEntries(updatedEntries);
      } else {
        // Add new product entry - this is now unique by product*price*storage
        const newEntry = {
          product: selectedProduct,
          quantity: Math.round(productQuantity * 100) / 100,
          customPrice: isCustomPrice ? price : null,
          storageId: selectedStorage?.StorageID || null,
          storageName: selectedStorage?.StorageName || null,
          entryId: Date.now() // Unique ID for this entry
        };
        
        setProductEntries(prev => [...prev, newEntry]);
      }
    }
    
    // Reset selection
    setSelectedProduct(null);
    setProductQuantity('');
    setProductUnitPrice('');
    setProductDialogOpen(false);
    setEditingProductId(null);
    setSelectedStorage(null);
    setAvailableStorages([]);
    setFormError(''); // Clear any error messages
  };
  
  // New handler for adding a product without closing the dialog
  const handleAddProductToCurrent = () => {
    if (!selectedProduct || isAddingProductRef.current) return;
    isAddingProductRef.current = true;
    setIsAddingProduct(true);

    if (!productQuantity || parseFloat(productQuantity) <= 0) {
      setFormError('נא להזין כמות');
      isAddingProductRef.current = false;
      setIsAddingProduct(false);
      return;
    }

    if (storageEnabled && noInventoryWarning) {
      setFormError(`לא ניתן להוסיף ${productLabel} ללא מלאי. יש לעדכן את המלאי תחילה.`);
      isAddingProductRef.current = false;
      setIsAddingProduct(false);
      return;
    }

    const price = parseFloat(productUnitPrice) || selectedProduct.Price;
    const isCustomPrice = Math.abs(Number(price) - Number(selectedProduct.Price)) > 0.001;

    // Validation for storage if enabled
    if (storageEnabled && selectedStorage) {
          const qty = parseFloat(productQuantity) || 0;
          if (qty > selectedStorage.available_quantity) {
            setFormError(`הכמות המבוקשת (${qty}) עולה על המלאי הזמין (${selectedStorage.available_quantity})`);
            isAddingProductRef.current = false;
            setIsAddingProduct(false);
            return;
          }
        }
    
    // Check if the product with same price AND same storage already exists in the current added products list
    const existingAddedIndex = addedProducts.findIndex(entry => {
      const sameProduct = entry.product.ProductID === selectedProduct.ProductID;
      const samePrice = ((!entry.customPrice && !isCustomPrice) || 
                        (entry.customPrice && isCustomPrice && Math.abs(Number(entry.customPrice) - Number(price)) < 0.001));
      const sameStorage = storageEnabled 
        ? entry.storageId === (selectedStorage?.StorageID || null)
        : true; // If storage not enabled, ignore storage in comparison
      
      return sameProduct && samePrice && sameStorage;
    });
    
    if (existingAddedIndex >= 0) {
      // Update quantity in the added products
      const updatedAddedProducts = [...addedProducts];
      updatedAddedProducts[existingAddedIndex].quantity = Math.round((parseFloat(updatedAddedProducts[existingAddedIndex].quantity) + parseFloat(productQuantity)) * 100) / 100;
      setAddedProducts(updatedAddedProducts);
    } else {
      // Add to temporary list as a new entry - unique by product*price*storage
      const newProduct = {
        product: selectedProduct,
        quantity: productQuantity,
        customPrice: isCustomPrice ? price : null,
        storageId: selectedStorage?.StorageID || null,
        storageName: selectedStorage?.StorageName || null,
        entryId: Date.now()
      };
      
      setAddedProducts(prev => [...prev, newProduct]);
    }
    
    // Reset selection for next product
    setSelectedProduct(null);
    setProductQuantity('');
    setProductUnitPrice('');
    setAvailableStorages([]);
    setSelectedStorage(null);
    setFormError(''); // Clear any error messages
    isAddingProductRef.current = false;
    setIsAddingProduct(false);

    // Open dropdown and focus search for next product selection
    setProductAutocompleteOpen(true);
    setTimeout(() => {
      productSearchInputRef.current?.focus();
    }, 100);
  };

  // New handler to finalize and add all products to the order
  const handleFinishAddingProducts = () => {
    if (isFinishingProductsRef.current) return; // Prevent double-click (sync check)
    isFinishingProductsRef.current = true;
    setIsFinishingProducts(true);

    // Process each product in the added products list
    addedProducts.forEach(newEntry => {
      // Check if this product*price*storage combination already exists in the main list
      const existingEntryIndex = productEntries.findIndex(entry => {
        const sameProduct = entry.product.ProductID === newEntry.product.ProductID;
        const samePrice = ((!entry.customPrice && !newEntry.customPrice) || 
                          (entry.customPrice && newEntry.customPrice && 
                          Math.abs(Number(entry.customPrice) - Number(newEntry.customPrice)) < 0.001));
        const sameStorage = storageEnabled 
          ? entry.storageId === newEntry.storageId
          : true; // If storage not enabled, ignore storage in comparison
        
        return sameProduct && samePrice && sameStorage;
      });
      
      if (existingEntryIndex >= 0) {
        // Update quantity of existing entry
        const updatedEntries = [...productEntries];
        updatedEntries[existingEntryIndex].quantity = Math.round((parseFloat(updatedEntries[existingEntryIndex].quantity) + parseFloat(newEntry.quantity)) * 100) / 100;
        setProductEntries(updatedEntries);
      } else {
        // Add as a new entry
        setProductEntries(prev => [...prev, newEntry]);
      }
    });
    
    // Reset and close
    setAddedProducts([]);
    setProductDialogOpen(false);
    setProductAutocompleteOpen(false);
    isFinishingProductsRef.current = false;
    setIsFinishingProducts(false);
  };

  // Handle cancel - clear added products too
  const handleCancelProductDialog = () => {
    setAddedProducts([]);
    setProductDialogOpen(false);
    setProductCreationMode(false);
    setCreateProductError('');
    setCreateProductSuccess('');
    setSelectedProduct(null);
    setNoInventoryWarning(false);
    setProductAutocompleteOpen(false);
  };
  
  // Clear a product from the added products list
  const removeFromAddedProducts = (index) => {
    setAddedProducts(prev => prev.filter((_, idx) => idx !== index));
  };
  
  const handleRemoveProduct = (index) => {
    setProductEntries(prev => prev.filter((_, idx) => idx !== index));
  };
  
  const handleDiscountTypeChange = (event) => {
    const newType = event.target.value;
    setDiscountType(newType);

    // Reset discount values when changing type
    if (newType === 'none') {
      setFormData(prev => ({
        ...prev,
        DiscountAmount: '', // Reset to empty string
        DiscountPercentage: '', // Reset to empty string
        DiscountReason: '',
      }));
    }
  };

  const handleTaxStateChange = (event) => {
    const newTaxState = event.target.value;
    setTaxState(newTaxState);

    // Save to localStorage
    localStorage.setItem(TAX_STATE_KEY, newTaxState);

    // Update formData based on tax state
    if (newTaxState === 'none') {
      setFormData(prev => ({
        ...prev,
        IsTaxable: false,
        IsTaxIncluded: false,
      }));
    } else if (newTaxState === 'included') {
      setFormData(prev => ({
        ...prev,
        IsTaxable: true,
        IsTaxIncluded: true,
      }));
    } else if (newTaxState === 'excluded') {
      setFormData(prev => ({
        ...prev,
        IsTaxable: true,
        IsTaxIncluded: false,
      }));
    }
  };

  const handleRoundingChange = (event) => {
    const enabled = event.target.checked;
    setRoundingEnabled(enabled);
    localStorage.setItem(ROUNDING_ENABLED_KEY, enabled.toString());
  };

  // Open payment dialog for adding or editing a payment
  const openPaymentDialog = (paymentIndex = null) => {
    if (paymentIndex !== null) {
      // Editing existing payment
      const paymentToEdit = formData.payments[paymentIndex];
      setPaymentAmount(paymentToEdit.amount.toString());
      setPaymentMethod(paymentToEdit.method);
      
      // Load payment method specific fields
      setPaymentMethodFields({
        checkNumber: paymentToEdit.checkNumber || '',
        checkDate: paymentToEdit.checkDate ? new Date(paymentToEdit.checkDate) : null,
        checkBankName: paymentToEdit.checkBankName || '',
        checkAccountNumber: paymentToEdit.checkAccountNumber || '',
        cardLastFour: paymentToEdit.cardLastFour || '',
        cardType: paymentToEdit.cardType || '',
        bankName: paymentToEdit.bankName || '',
        bankAccountLast4: paymentToEdit.bankAccountLast4 || '',
        transferDate: paymentToEdit.transferDate ? new Date(paymentToEdit.transferDate) : null
      });
      
      setEditingPaymentIndex(paymentIndex);
    } else {
      // Adding new payment
      setPaymentAmount('0');
      setPaymentMethod('cash');
      setPaymentMethodFields({
        checkNumber: '',
        checkDate: null,
        checkBankName: '',
        checkAccountNumber: '',
        cardLastFour: '',
        cardType: '',
        bankName: '',
        bankAccountLast4: '',
        transferDate: null
      });
      setEditingPaymentIndex(null);
    }
    
    setFormError('');
    setAddPaymentOpen(true);
  };
  
  // Handle payment addition or update
  const handleAddPayment = () => {
    if (!paymentAmount || isNaN(parseFloat(paymentAmount)) || parseFloat(paymentAmount) <= 0) {
      setFormError('יש להזין סכום תשלום תקין');
      return;
    }

    // Check if payment exceeds total amount
    const newPaymentAmount = parseFloat(paymentAmount);
    let totalPaidAfterThisPayment;

    if (editingPaymentIndex !== null) {
      // When editing, subtract the old payment and add the new one
      const oldPaymentAmount = formData.payments[editingPaymentIndex].amount;
      totalPaidAfterThisPayment = formData.PaidAmount - oldPaymentAmount + newPaymentAmount;
    } else {
      // When adding new payment
      totalPaidAfterThisPayment = formData.PaidAmount + newPaymentAmount;
    }

    if (totalPaidAfterThisPayment > formData.TotalAmount) {
      const maxAllowed = formData.TotalAmount - (formData.PaidAmount - (editingPaymentIndex !== null ? formData.payments[editingPaymentIndex].amount : 0));
      setFormError(`סכום התשלום חורג מהסכום הכולל. ניתן להוסיף עד ${formatCurrency(maxAllowed)}`);
      return;
    }

    // For check payments, validate check number
    // if (paymentMethod === 'check' && !paymentMethodFields.checkNumber) {
    //   setFormError('יש להזין מספר צ\'ק');
    //   return;
    // }

    // Prepare payment object
    const paymentData = {
      amount: parseFloat(paymentAmount),
      method: paymentMethod,
      notes: '',
      // Add method-specific fields
      ...paymentMethod === 'check' && {
        checkNumber: paymentMethodFields.checkNumber,
        checkDate: paymentMethodFields.checkDate,
        checkBankName: paymentMethodFields.checkBankName,
        checkAccountNumber: paymentMethodFields.checkAccountNumber,
      },
      ...paymentMethod === 'credit_card' && {
        cardLastFour: paymentMethodFields.cardLastFour,
        cardType: paymentMethodFields.cardType,
      },
      ...paymentMethod === 'bank_transfer' && {
        bankName: paymentMethodFields.bankName,
        bankAccountLast4: paymentMethodFields.bankAccountLast4,
        transferDate: paymentMethodFields.transferDate,
      }
    };
    
    if (editingPaymentIndex !== null) {
      // Update existing payment
      const updatedPayments = [...formData.payments];
      updatedPayments[editingPaymentIndex] = paymentData;
      
      setFormData(prev => ({
        ...prev,
        payments: updatedPayments,
      }));
    } else {
      // Add new payment
      setFormData(prev => ({
        ...prev,
        payments: [...prev.payments, paymentData],
      }));
    }
    
    // Close dialog and reset
    setAddPaymentOpen(false);
    setFormError('');
    setEditingPaymentIndex(null);
  };
  
  // Handle payment deletion
  const handleDeletePayment = (index) => {
    setFormData(prev => ({
      ...prev,
      payments: prev.payments.filter((_, i) => i !== index),
    }));
  };
  
  // Get payment method display name
  const getPaymentMethodName = (method) => {
    switch (method) {
      case 'cash':
        return 'מזומן';
      case 'credit_card':
        return 'כרטיס אשראי';
      case 'bank_transfer':
        return 'העברה בנקאית';
      case 'check':
        return 'צ\'ק';
      case 'other':
        return 'אחר';
      default:
        return method;
    }
  };
  
  // Get payment method icon
  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case 'cash':
        return <AttachMoneyIcon />;
      case 'credit_card':
        return <CreditCardIcon />;
      case 'bank_transfer':
        return <AccountBalanceIcon />;
      case 'check':
        return <ReceiptIcon />;
      case 'other':
        return <MoreHorizIcon />;
      default:
        return <PaymentIcon />;
    }
  };
  
  // Format date to local string
  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('he-IL');
  };
  
  // Get payment details for display
  const getPaymentDetails = (payment) => {
    const details = [];
    
    if (payment.method === 'check') {
      if (payment.checkNumber) details.push(`מס' צ'ק: ${payment.checkNumber}`);
      if (payment.checkBankName) details.push(`בנק: ${payment.checkBankName}`);
      if (payment.checkAccountNumber) details.push(`חשבון: ${payment.checkAccountNumber}`);
      if (payment.checkDate) details.push(`תאריך: ${formatDate(payment.checkDate)}`);
    } else if (payment.method === 'credit_card') {
      if (payment.cardType) details.push(`סוג: ${payment.cardType}`);
      if (payment.cardLastFour) details.push(`מסתיים ב: ${payment.cardLastFour}`);
    } else if (payment.method === 'bank_transfer') {
      if (payment.bankName) details.push(`בנק: ${payment.bankName}`);
      if (payment.bankAccountLast4) details.push(`חשבון: ${payment.bankAccountLast4}`);
      if (payment.transferDate) details.push(`תאריך: ${formatDate(payment.transferDate)}`);
    }
    
    return details.join(' | ');
  };
  
  const handleSubmit = () => {
    // Prevent double submission
    if (isSubmitting || createOrderMutation.isLoading) {
      return;
    }
    
    setIsSubmitting(true);
    setFormError('');
    
    // Validation
    if (!formData.ClientBusinessID) {
      setFormError('יש לבחור לקוח');
      setActiveStep(0);
      setIsSubmitting(false);
      return;
    }
    
    if (productEntries.length === 0) {
      setFormError(`יש להוסיף לפחות ${productLabel} אחד ${orderLabel}`);
      setActiveStep(1);
      setIsSubmitting(false);
      return;
    }
    
    // Prepare data for submission
    const orderData = {
        ClientBusinessID: formData.ClientBusinessID,
        OrderDate: format(formData.OrderDate, 'yyyy-MM-dd'),
        Status: formData.Status,
        TotalAmount: Math.round(formData.TotalAmount * 100) / 100,
        Subtotal: Math.round(formData.Subtotal * 100) / 100,
        Notes: formData.Comments || '',
        items: productEntries.map(entry => ({
          ProductID: entry.product.ProductID,
          Quantity: Math.round(entry.quantity * 100) / 100,
          UnitPrice: Math.round((parseFloat(entry.customPrice || entry.product.Price) || 0) * 100) / 100,
          ...(storageEnabled && entry.storageId && { StorageID: entry.storageId })
        })),
    };
    
    // Map payments to API format if any exist
    if (formData.payments.length > 0) {
      orderData.payments = formData.payments.map(payment => {
        const paymentData = {
          Amount: payment.amount,
          PaymentMethod: payment.method,
          Notes: payment.notes || '',
        };
        
        // Add method-specific fields
        if (payment.method === 'check') {
          if (payment.checkNumber) paymentData.CheckNumber = payment.checkNumber;
          if (payment.checkDate) paymentData.CheckDate = format(new Date(payment.checkDate), 'yyyy-MM-dd');
          if (payment.checkBankName) paymentData.CheckBankName = payment.checkBankName;
          if (payment.checkAccountNumber) paymentData.CheckAccountNumber = payment.checkAccountNumber;
        } else if (payment.method === 'credit_card') {
          if (payment.cardLastFour) paymentData.CardLastFour = payment.cardLastFour;
          if (payment.cardType) paymentData.CardType = payment.cardType;
        } else if (payment.method === 'bank_transfer') {
          if (payment.bankName) paymentData.BankName = payment.bankName;
          if (payment.bankAccountLast4) paymentData.BankAccountLast4 = payment.bankAccountLast4;
          if (payment.transferDate) paymentData.TransferDate = format(new Date(payment.transferDate), 'yyyy-MM-dd');
        }
        
        return paymentData;
      });
    }
    
    // Add optional fields if they have values
    if (formData.DueDate) {
      orderData.DueDate = format(formData.DueDate, 'yyyy-MM-dd');
    }
    
    if (formData.InvoiceNumber) {
      orderData.InvoiceNumber = formData.InvoiceNumber;
    }
    
    // Add discount fields if discount is applied
    if (discountType === 'amount' && formData.DiscountAmount) {
      const discountValue = Math.round((parseFloat(formData.DiscountAmount) || 0) * 100) / 100;
      if (discountValue > 0) {
        orderData.DiscountAmount = discountValue;
        if (formData.DiscountReason) {
          orderData.DiscountReason = formData.DiscountReason;
        }
      }
    } else if (discountType === 'percentage' && formData.DiscountPercentage) {
      const percentageValue = Math.round((parseFloat(formData.DiscountPercentage) || 0) * 100) / 100;
      if (percentageValue > 0) {
        orderData.DiscountPercentage = percentageValue;
        if (formData.DiscountReason) {
          orderData.DiscountReason = formData.DiscountReason;
        }
      }
    }

    // Add tax fields
    orderData.IsTaxable = formData.IsTaxable;
    orderData.IsTaxIncluded = formData.IsTaxIncluded;
    orderData.TaxRate = Math.round((formData.TaxRate || 18.00) * 100) / 100;

    // Add rounding enabled flag
    orderData.RoundingEnabled = roundingEnabled;

    // Submit
    createOrderMutation.mutate(orderData);
  };
  
  const handleNext = () => {
    // Validate current step
    if (activeStep === 0 && !formData.ClientBusinessID) {
      setFormError('יש לבחור לקוח');
      return;
    }
    
    if (activeStep === 1 && productEntries.length === 0) {
      setFormError(`יש להוסיף לפחות ${productLabel} אחד ${orderLabel}`);
      return;
    }
    
    setFormError('');
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };
  
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    setFormError('');
  };

  // Content for each step
  const renderStepContent = (step) => {
    switch (step) {
      case 0: // Client selection
        return (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              בחר לקוח עבור {orderLabel}
            </Typography>
            
            {/* Box that wraps both the autocomplete and the new client button */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
            <Autocomplete
                freeSolo
                options={clientAutocompleteOptions}
                value={selectedClient}
                open={clientAutocompleteOpen}
                onOpen={() => setClientAutocompleteOpen(true)}
                onClose={() => setClientAutocompleteOpen(false)}
                slotProps={{
                  popper: {
                    placement: 'bottom-start',
                    modifiers: [{ name: 'flip', enabled: false }],
                  }
                }}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  if (option.isNoResults) return option.ClientName; // Show "אין תוצאות"
                  return `${option.ClientName} (${option.ClientBusinessID})`;
                }}
                getOptionDisabled={(option) => option.isNoResults === true}  // Make "no results" unselectable
                onChange={(event, newValue) => {
                  // Ignore dummy "no results" record
                  if (newValue && typeof newValue === 'object' && newValue.isNoResults) {
                    return;
                  }

                  handleClientSelect(event, newValue);
                }}
                onInputChange={(event, newInputValue, reason) => {
                  // Clear previous timer
                  if (autocompleteTimerCleanup.current) {
                    autocompleteTimerCleanup.current();
                  }
                  // Debounce the API call
                  autocompleteTimerCleanup.current = debouncedSetAutocompleteSearch(newInputValue);

                  // Clear selectedClient if input is emptied (manually deleted all text)
                  if (newInputValue.trim() === '' && selectedClient) {
                    setSelectedClient(null);
                    setFormData(prev => ({
                      ...prev,
                      ClientBusinessID: '',
                      ClientName: '',
                    }));
                  }
                }}
                isOptionEqualToValue={(option, value) => option?.ClientBusinessID === value?.ClientBusinessID}
                filterOptions={(x) => x}  // Disable client-side filtering (we do server-side)
                loading={isLoadingClients}
                loadingText="טוען..."
                renderInput={(params) => (
                <TextField
                    {...params}
                    autoFocus
                    label="חפש לקוח"
                    variant="outlined"
                    required
                    error={!!formError && !formData.ClientBusinessID}
                    helperText={formError && !formData.ClientBusinessID ? formError : ''}
                    InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                        <>
                        <InputAdornment position="start">
                            <SearchIcon />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                        </>
                    ),
                    }}
                />
                )}
                renderOption={(props, option) => (
                <li {...props}>
                    <Box>
                    <Typography variant="body1">{option.ClientName}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        מזהה לקוח: {option.ClientBusinessID}
                    </Typography>
                    </Box>
                </li>
                )}
                noOptionsText="לא נמצאו לקוחות"
                sx={{ width: 'auto', minWidth: '400px' }}
            />
            
            {/* "Create New Client" button */}
            <Button
                variant="contained"
                color="primary"
                onClick={handleOpenCreateClientDialog}
                startIcon={<AddIcon />}
                disabled={isLoadingClients}
                sx={{ direction: "ltr" }}
            >
                לקוח חדש
            </Button>
            </Box>
            
            {selectedClient && (
              <Paper elevation={1} sx={{ mt: 3, p: 2 }}>
                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                  פרטי לקוח נבחר
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">שם לקוח:</Typography>
                    <Typography variant="body1">{selectedClient.ClientName}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">מזהה לקוח:</Typography>
                    <Typography variant="body1">{selectedClient.ClientBusinessID}</Typography>
                  </Grid>
                  {selectedClient.ClientPhoneNumber && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">טלפון:</Typography>
                      <Typography variant="body1">{selectedClient.ClientPhoneNumber}</Typography>
                    </Grid>
                  )}
                  {selectedClient.ClientAddress && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">כתובת:</Typography>
                      <Typography variant="body1">{selectedClient.ClientAddress}</Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            )}
          </Box>
        );
      
      case 1: // Product selection
        return (
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                {productPluralLabel} ב{orderLabel}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => openProductDialog()}
                sx={{direction: 'ltr'}}
              >
                הוסף {productPluralLabel}
              </Button>
            </Box>
            
            {productEntries.length > 0 ? (
              <>
                <TableContainer component={Paper} sx={{ mb: 4 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>שם {productLabel}</TableCell>
                        <TableCell align="center">כמות</TableCell>
                        {storageEnabled && <TableCell align="center">מחסן</TableCell>}
                        <TableCell align="right">מחיר יחידה</TableCell>
                        <TableCell align="right">סה"כ</TableCell>
                        <TableCell align="center">פעולות</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {productEntries.map((entry, index) => {
                        const actualPrice = entry.customPrice || entry.product.Price;
                        
                        return (
                          <TableRow key={entry.entryId || index}>
                            <TableCell>{entry.product.ProductName}</TableCell>
                            <TableCell align="center">{entry.quantity}</TableCell>
                            {storageEnabled && (
                              <TableCell align="center">
                                {entry.storageName || 'ברירת מחדל'}
                              </TableCell>
                            )}
                            <TableCell align="right">
                              {entry.customPrice !== null && Number(entry.customPrice) !== Number(entry.product.Price) ? (
                                <Box sx={{ display:'flex', alignItems:'right' }}>
                                  <Typography sx={{ textDecoration:'line-through', mr:1 }}>
                                    {formatCurrency(entry.product.Price)}
                                  </Typography>
                                  <Typography sx={{marginRight: 2 }}>{formatCurrency(entry.customPrice)}</Typography>
                                </Box>
                              ) : (
                                <Typography sx={{marginRight: 2 }}>{formatCurrency(entry.product.Price)}</Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {new Intl.NumberFormat('he-IL', {
                                style: 'currency',
                                currency: 'ILS',
                              }).format(actualPrice * entry.quantity)}
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="ערוך">
                                <IconButton
                                  color="primary"
                                  size="small"
                                  onClick={() => openProductDialog(index)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="הסר">
                                <IconButton
                                  color="error"
                                  size="small"
                                  onClick={() => handleRemoveProduct(index)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                {/* Order Summary - Discount details moved to payment section */}
                <Paper elevation={3} sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle1">
                      סה"כ לפני הנחה:
                    </Typography>
                    <Typography variant="subtitle1">
                      {new Intl.NumberFormat('he-IL', {
                        style: 'currency',
                        currency: 'ILS',
                      }).format(formData.Subtotal)}
                    </Typography>
                  </Box>
                  
                  {(discountType === 'amount' && formData.DiscountAmount > 0) ||
                   (discountType === 'percentage' && formData.DiscountPercentage > 0) ? (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="subtitle1">
                        הנחה{discountType === 'percentage' ? ` (${formData.DiscountPercentage}%)` : ''}:
                      </Typography>
                      <Typography variant="subtitle1" color="error">
                        -{new Intl.NumberFormat('he-IL', {
                          style: 'currency',
                          currency: 'ILS',
                        }).format(formData.DiscountAmount)}
                      </Typography>
                    </Box>
                  ) : null}

                  {/* Tax breakdown */}
                  {taxState === 'included' && formData.TaxAmount > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        מזה מע"מ ({formData.TaxRate}%):
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatCurrency(formData.TaxAmount)}
                      </Typography>
                    </Box>
                  )}

                  {taxState === 'excluded' && formData.TaxAmount > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="subtitle1">
                        מע"מ ({formData.TaxRate}%):
                      </Typography>
                      <Typography variant="subtitle1">
                        +{formatCurrency(formData.TaxAmount)}
                      </Typography>
                    </Box>
                  )}

                  <Divider sx={{ my: 1 }} />

                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      סה"כ לתשלום:
                    </Typography>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {new Intl.NumberFormat('he-IL', {
                        style: 'currency',
                        currency: 'ILS',
                      }).format(formData.TotalAmount)}
                    </Typography>
                  </Box>
                </Paper>
              </>
            ) : (
              <Alert severity="info" sx={{ mt: 2 }}>
                אין {productPluralLabel} ב{orderLabel}. יש להוסיף לפחות {productLabel} אחד.
              </Alert>
            )}
            
            {/* Enhanced Product Selection/Edit Dialog with Create & Multi-Add */}
            <MobileDialog
              open={productDialogOpen}
              onClose={(event, reason) => {
                  if (reason === 'backdropClick') {
                    return; // Prevent closing on backdrop click
                  }
                  handleCancelProductDialog();
                }}
              fullWidth
              maxWidth="md"
              PaperProps={{
                sx: { minHeight: '60vh' }
              }}
            >
              <DialogTitle>
                {editingProductId !== null ? `עריכת ${productLabel}` : (productCreationMode ? `יצירת ${productLabel} חדש` : `הוספת ${productPluralLabel} ${orderLabel}`)}
              </DialogTitle>
              <DialogContent>
                <Box sx={{ py: 1 }}>
                  {/* PRODUCT CREATION MODE */}
                  {formError && (
                    <Alert 
                      severity="error" 
                      sx={{ mb: 2 }} 
                      onClose={() => setFormError('')}
                    >
                      {formError}
                    </Alert>
                  )}
                  {productCreationMode ? (
                    <Box sx={{ mt: 1 }}>
                      {createProductError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                          {createProductError}
                        </Alert>
                      )}
                      {createProductSuccess && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                          {createProductSuccess}
                        </Alert>
                      )}
                      
                      {/* Product Name - Always full width */}
                      <TextField
                        autoFocus
                        name="ProductName"
                        label={productFieldsSchema.ProductName?.label || `שם ${productLabel}`}
                        value={newProductData.ProductName}
                        onChange={handleNewProductChange}
                        variant="outlined"
                        fullWidth
                        required
                        sx={{ mb: 2 }}
                      />
                      
                      {/* Two-column grid for other fields EXCEPT Price and Quantity */}
                      <Grid container spacing={2}>
                        {/* CatalogNumber if configured */}
                        {productFields.includes('CatalogNumber') && (
                          <Grid item xs={12} sm={6}>
                            <TextField
                              name="CatalogNumber"
                              label={productFieldsSchema.CatalogNumber?.label || "מק״ט"}
                              value={newProductData.CatalogNumber || ''}
                              onChange={handleNewProductChange}
                              variant="outlined"
                              fullWidth
                              required={productFieldsSchema.CatalogNumber?.required}
                            />
                          </Grid>
                        )}
                        
                        {/* Barcode if configured */}
                        {productFields.includes('Barcode') && (
                          <Grid item xs={12} sm={6}>
                            <TextField
                              name="Barcode"
                              label={productFieldsSchema.Barcode?.label || "ברקוד"}
                              value={newProductData.Barcode || ''}
                              onChange={handleNewProductChange}
                              variant="outlined"
                              fullWidth
                              required={productFieldsSchema.Barcode?.required}
                            />
                          </Grid>
                        )}
                        
                        {/* Other dynamic fields (excluding Price, Quantity, Description) */}
                        {productFields
                          .filter(field => 
                            !['ProductName', 'CatalogNumber', 'Barcode', 'Price', 'Quantity', 'Description'].includes(field) &&
                            !excludedFields.includes(field) &&
                            productFieldsSchema[field] &&
                            !productFieldsSchema[field].readonly
                          )
                          .map(fieldName => {
                            const fieldConfig = productFieldsSchema[fieldName];
                            return renderProductField(fieldName, fieldConfig);
                          })}
                        
                        {/* Price and Quantity - Always together as the last row before Description */}
                        <Grid item xs={12} sm={6}>
                          <TextField
                            name="Price"
                            label={productFieldsSchema.Price?.label || "מחיר"}
                            type="number"
                            value={newProductData.Price}
                            onFocus={(e) => e.target.select()}
                            onChange={handleNewProductChange}
                            variant="outlined"
                            fullWidth
                            required
                            InputProps={{
                              startAdornment: <InputAdornment position="start">₪</InputAdornment>,
                              inputProps: { min: 0, step: 0.01 }
                            }}
                          />
                        </Grid>
                        
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="כמות להזמנה"
                            name="Quantity"
                            type="number"
                            onFocus={(e) => e.target.select()}
                            value={newProductData.Quantity}
                            onChange={handleNewProductChange}
                            variant="outlined"
                            fullWidth
                            required
                            InputProps={{
                              inputProps: { 
                                min: 0.01,
                                step: 0.01
                              }
                            }}
                          />
                        </Grid>
                      </Grid>
                      
                      {/* Description - Always full width at the bottom */}
                      {(productFields.includes('Description') || !productFields.length) && (
                        <TextField
                          name="Description"
                          label={productFieldsSchema.Description?.label || "תיאור"}
                          value={newProductData.Description}
                          onChange={handleNewProductChange}
                          variant="outlined"
                          fullWidth
                          multiline
                          rows={2}
                          sx={{ mt: 2 }}
                        />
                      )}
                      
                      {/* Inventory Management Section - Only shown if storage is enabled */}
                      {storageEnabled && allStorages.length > 0 && (
                        <Box sx={{ mt: 3 }}>
                          <Divider sx={{ mb: 2 }} />
                          
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                            הגדרת מלאי התחלתי
                          </Typography>
                          
                          <Alert severity="info" sx={{ mb: 2 }}>
                            <Typography variant="body2">
                              הגדר כמות מלאי התחלתית עבור כל מחסן. ניתן להשאיר 0 במחסנים ללא מלאי.
                            </Typography>
                          </Alert>
                          
                          <Grid container spacing={2}>
                            {allStorages.map(storage => (
                              <Grid item xs={12} sm={6} key={storage.StorageID}>
                                <TextField
                                  label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      {storage.StorageName}
                                      {storage.is_default && (
                                        <Chip label="ברירת מחדל" size="small" color="primary" />
                                      )}
                                    </Box>
                                  }
                                  type="number"
                                  value={newProductInventory[storage.StorageID]?.quantity || 0}
                                  onChange={(e) => {
                                    const value = parseFloat(e.target.value) || 0;
                                    setNewProductInventory(prev => ({
                                      ...prev,
                                      [storage.StorageID]: {
                                        ...prev[storage.StorageID],
                                        quantity: value
                                      }
                                    }));
                                  }}
                                  onFocus={(e) => e.target.select()}
                                  variant="outlined"
                                  fullWidth
                                  InputProps={{
                                    inputProps: { min: 0, step: 0.01 }
                                  }}
                                  helperText={storage.Address || undefined}
                                />
                              </Grid>
                            ))}
                          </Grid>
                          
                          {/* Total inventory display */}
                          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              סה"כ מלאי: {Object.values(newProductInventory).reduce(
                                (sum, data) => sum + (parseFloat(data.quantity) || 0), 0
                              )}
                            </Typography>
                          </Box>
                          
                          <Divider sx={{ my: 2 }} />
                          
                          {/* Storage selection for order item */}
                          <FormControl fullWidth required>
                            <InputLabel>בחר מחסן עבור פריט ההזמנה</InputLabel>
                            <Select
                              value={selectedStorageForNewProduct?.StorageID || ''}
                              onChange={(e) => {
                                const storage = allStorages.find(s => s.StorageID === e.target.value);
                                setSelectedStorageForNewProduct(storage);
                              }}
                              label="בחר מחסן עבור פריט ההזמנה"
                            >
                              {allStorages
                                .filter(storage => {
                                  const inventory = newProductInventory[storage.StorageID];
                                  return inventory && inventory.quantity > 0;
                                })
                                .map(storage => {
                                  const inventory = newProductInventory[storage.StorageID];
                                  return (
                                    <MenuItem key={storage.StorageID} value={storage.StorageID}>
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          {storage.is_default && <HomeIcon sx={{ fontSize: 16 }} />}
                                          <Typography>{storage.StorageName}</Typography>
                                        </Box>
                                        <Chip 
                                          label={`זמין: ${inventory.quantity}`} 
                                          size="small"
                                          color="success"
                                          variant="outlined"
                                        />
                                      </Box>
                                    </MenuItem>
                                  );
                                })}
                            </Select>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                              * רק מחסנים עם מלאי זמין מוצגים
                            </Typography>
                          </FormControl>
                        </Box>
                      )}
                      
                      {/* Show total calculation */}
                      {newProductData.Price && newProductData.Quantity && !isNaN(parseFloat(newProductData.Price)) && !isNaN(parseFloat(newProductData.Quantity)) && (
                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="subtitle1">
                            סה"כ לפריט:
                          </Typography>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {formatCurrency(parseFloat(newProductData.Price) * parseFloat(newProductData.Quantity))}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )  : (
                    <>
                      {/* PRODUCT SELECTION MODE */}
                      {!editingProductId && !productCreationMode && (
                        <>
                        <Autocomplete
                          options={products}
                          getOptionLabel={(option) => option.ProductName}
                          value={selectedProduct}
                          onChange={handleProductSelect}
                          filterOptions={filterProductsEnhanced}
                          disableListWrap
                          open={productAutocompleteOpen}
                          onOpen={() => setProductAutocompleteOpen(true)}
                          onClose={() => setProductAutocompleteOpen(false)}
                          renderInput={(params) => {
                            // Dynamically build placeholder text based on available fields
                            const getPlaceholderText = () => {
                              const searchFields = [`שם ${productLabel}`];
                              if (productFields.includes('CatalogNumber')) {
                                searchFields.push(productFieldsSchema.CatalogNumber?.label || 'מק"ט');
                              }
                              if (productFields.includes('Barcode')) {
                                searchFields.push(productFieldsSchema.Barcode?.label || 'ברקוד');
                              }
                              
                              return searchFields.length > 1 
                                ? `הקלד ${searchFields.join(', ')}` 
                                : `הקלד שם ${productLabel}`;
                            };
                            
                            return (
                              <TextField
                                {...params}
                                inputRef={productSearchInputRef}
                                label={`חפש ובחר ${productLabel}`}
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                autoFocus
                                placeholder={getPlaceholderText()}
                                helperText={params.inputProps.value?.length === 1 ? "הקלד לפחות 2 תווים לחיפוש" : ""}
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment: (
                                    <>
                                      <InputAdornment position="start">
                                        <SearchIcon />
                                      </InputAdornment>
                                      {params.InputProps.startAdornment}
                                    </>
                                  )
                                }}
                              />
                            );
                          }}
                          renderOption={(props, option) => {
                            // Enhanced rendering
                            const showBarcode = productFields.includes('Barcode');
                            const showCatalogNumber = productFields.includes('CatalogNumber');
                            const showEnhanced = showBarcode || showCatalogNumber || storageEnabled;
                            
                            if (!showEnhanced) {
                              // Simple rendering
                              return (
                                <li {...props}>
                                  <Box>
                                    <Typography variant="body1">{option.ProductName}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      מחיר: {new Intl.NumberFormat('he-IL', {
                                        style: 'currency',
                                        currency: 'ILS',
                                      }).format(option.Price)}
                                    </Typography>
                                    {option.Description && (
                                      <Typography variant="body2" color="text.secondary" noWrap>
                                        {option.Description}
                                      </Typography>
                                    )}
                                  </Box>
                                </li>
                              );
                            }
                            
                            // Enhanced rendering
                            return (
                              <li {...props}>
                                <Box sx={{ width: '100%' }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body1">{option.ProductName}</Typography>
                                    {option.matchedField && (
                                      <Chip 
                                        label={option.matchedField} 
                                        size="small" 
                                        variant="outlined"
                                        color="primary"
                                        sx={{ ml: 1 }}
                                      />
                                    )}
                                  </Box>
                                  <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                                    {showCatalogNumber && option.CatalogNumber && (
                                      <Typography variant="caption" color="text.secondary">
                                        {productFieldsSchema.CatalogNumber?.label || 'מק"ט'}: {option.CatalogNumber}
                                      </Typography>
                                    )}
                                    {showBarcode && option.Barcode && (
                                      <Typography variant="caption" color="text.secondary">
                                        {productFieldsSchema.Barcode?.label || 'ברקוד'}: {option.Barcode}
                                      </Typography>
                                    )}
                                    {option.Price !== undefined && (
                                      <Typography variant="caption" color="text.secondary">
                                        מחיר: {new Intl.NumberFormat('he-IL', {
                                          style: 'currency',
                                          currency: 'ILS',
                                        }).format(option.Price)}
                                      </Typography>
                                    )}
                                    {storageEnabled && option.total_quantity !== undefined && (
                                      <Typography 
                                        variant="caption" 
                                        color={option.total_quantity > 0 ? 'success.main' : 'error.main'}
                                        sx={{ fontWeight: 'bold' }}
                                      >
                                        מלאי: {option.total_quantity}
                                      </Typography>
                                    )}
                                  </Box>
                                  {storageEnabled && option.inventory && option.inventory.length > 0 && (
                                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                                      {option.inventory
                                        .filter(inv => inv.available_quantity > 0)
                                        .map((inv) => (
                                          <Chip
                                            key={inv.StorageID}
                                            label={`${inv.StorageName}: ${inv.available_quantity}`}
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                          />
                                        ))}
                                    </Box>
                                  )}
                                </Box>
                              </li>
                            );
                          }}
                          loading={isLoadingProducts}
                          loadingText={`טוען ${productPluralLabel}...`}
                          noOptionsText={`לא נמצאו ${productPluralLabel}`}
                        />
                        </>
                      )}
                      
                      {selectedProduct && !productCreationMode && (
                        <Box sx={{ mt: 2 }}>
                          <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
                            {storageEnabled && noInventoryWarning && (
                              <InventoryWarningAlert 
                                product={selectedProduct}
                                onOpenProductPage={() => {
                                  // Open product detail page in new window
                                  window.open(`/product/${selectedProduct.ProductID}`, '_blank');
                                }}
                              />
                            )}

                            <Typography variant="subtitle1" fontWeight="bold">
                              {selectedProduct.ProductName}
                            </Typography>
                            
                            {/* Show additional product fields */}
                            <Box sx={{ mt: 1 }}>
                              {productFields.includes('CatalogNumber') && selectedProduct.CatalogNumber && (
                                <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    {productFieldsSchema.CatalogNumber?.label || 'מק"ט'}:
                                  </Typography>
                                  <Typography variant="body2" fontWeight="medium">
                                    {selectedProduct.CatalogNumber}
                                  </Typography>
                                </Box>
                              )}
                              
                              {productFields.includes('Barcode') && selectedProduct.Barcode && (
                                <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    {productFieldsSchema.Barcode?.label || 'ברקוד'}:
                                  </Typography>
                                  <Typography variant="body2" fontWeight="medium">
                                    {selectedProduct.Barcode}
                                  </Typography>
                                </Box>
                              )}
                              
                              {selectedProduct.Description && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                  {selectedProduct.Description}
                                </Typography>
                              )}
                              
                              <Typography variant="body2">
                                מחיר מקורי: {new Intl.NumberFormat('he-IL', {
                                  style: 'currency',
                                  currency: 'ILS',
                                }).format(selectedProduct.Price)}
                              </Typography>
                              
                              {/* Show inventory information if storage is enabled */}
                              {selectedProduct && !productCreationMode && storageEnabled && availableStorages.length > 0 && (
                                <Grid item xs={12}>
                                  <FormControl fullWidth sx={{ mt: 2 }}>
                                    <InputLabel>בחר מחסן</InputLabel>
                                    <Select
                                      value={selectedStorage?.StorageID || ''}
                                      onChange={(e) => {
                                        const storage = availableStorages.find(s => s.StorageID === e.target.value);
                                        setSelectedStorage(storage);
                                        // Update max quantity based on selected storage
                                        if (storage && productQuantity > storage.available_quantity) {
                                          setProductQuantity(storage.available_quantity);
                                        }
                                      }}
                                      label="בחר מחסן"
                                      required
                                    >
                                      {availableStorages.map(storage => (
                                        <MenuItem key={storage.StorageID} value={storage.StorageID}>
                                          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                              {storage.is_default && <HomeIcon sx={{ fontSize: 16 }} />}
                                              <Typography>{storage.StorageName}</Typography>
                                            </Box>
                                            <Chip 
                                              label={`זמין: ${storage.available_quantity}`} 
                                              size="small"
                                              color={storage.available_quantity > 0 ? "success" : "default"}
                                              variant="outlined"
                                            />
                                          </Box>
                                        </MenuItem>
                                      ))}
                                    </Select>
                                    {selectedStorage && (
                                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                                        מלאי זמין במחסן זה: {selectedStorage.available_quantity} יחידות
                                      </Typography>
                                    )}
                                  </FormControl>
                                </Grid>
                              )}
                            </Box>
                          </Paper>
                          
                          <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                inputRef={quantityInputRef}
                                label="כמות"
                                type="number"
                                variant="outlined"
                                fullWidth
                                value={productQuantity}
                                onFocus={(e) => e.target.select()}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    unitPriceInputRef.current?.focus();
                                  }
                                }}
                                onChange={(e) => {
                                  setProductQuantity(e.target.value);
                                  setFormError(''); // Clear any previous errors
                                }}
                                InputProps={{
                                  inputProps: {
                                    min: 0.01,
                                    step: 0.01,
                                  }
                                }}
                                helperText={
                                  storageEnabled && selectedStorage && productQuantity > selectedStorage.available_quantity
                                    ? `הכמות המקסימלית הזמינה: ${selectedStorage.available_quantity}`
                                    : ''
                                }
                                error={storageEnabled && selectedStorage && productQuantity > selectedStorage.available_quantity}
                              />
                            </Grid>
                            
                            <Grid item xs={12} sm={6}>
                              <TextField
                                inputRef={unitPriceInputRef}
                                label="מחיר יחידה"
                                type="number"
                                variant="outlined"
                                fullWidth
                                value={productUnitPrice}
                                onFocus={(e) => e.target.select()}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    addProductButtonRef.current?.focus();
                                  }
                                }}
                                onChange={(e) => setProductUnitPrice(e.target.value)}
                                InputProps={{
                                  inputProps: { min: 0, step: 0.01 },
                                  startAdornment: <InputAdornment position="start">₪</InputAdornment>,
                                }}
                                helperText={
                                  productUnitPrice !== selectedProduct.Price.toString()
                                    ? 'מחיר שונה מהמחיר המקורי'
                                    : ''
                                }
                              />
                            </Grid>
                          </Grid>
                          
                          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="subtitle1">
                              סה"כ:
                            </Typography>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {new Intl.NumberFormat('he-IL', {
                                style: 'currency',
                                currency: 'ILS',
                              }).format((parseFloat(productUnitPrice) || selectedProduct.Price) * productQuantity)}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </>
                  )}
                  
                  {/* Display list of products added in the current session */}
                  {!editingProductId && addedProducts.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                        {productPluralLabel} שנוספו ({addedProducts.length}):
                      </Typography>
                      <Paper variant="outlined" sx={{ mb: 2 }}>
                        <List dense>
                          {addedProducts.map((entry, index) => (
                            <ListItem 
                              key={entry.entryId || index}
                              secondaryAction={
                                <IconButton 
                                  edge="end" 
                                  aria-label="delete"
                                  onClick={() => removeFromAddedProducts(index)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              }
                            >
                              <ListItemText
                                primary={`${entry.product.ProductName} (${entry.quantity} יח')`}
                                secondary={`${formatCurrency(entry.customPrice || entry.product.Price)} ליחידה | סה"כ: ${formatCurrency((entry.customPrice || entry.product.Price) * entry.quantity)}`}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Paper>
                    </Box>
                  )}
                </Box>
              </DialogContent>
              <DialogActions sx={{ 
                flexDirection: 'column',
                padding: 0,
                gap: 0
              }}>
                {/* Intermediate actions section - only shown in add mode */}
                {!productCreationMode && !editingProductId && (
                  <Box sx={{ 
                    width: '100%',
                    padding: 2,
                    borderBottom: '2px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 2
                  }}>
                    <Button 
                      onClick={toggleProductCreationMode} 
                      color="secondary"
                      variant="contained" 
                      startIcon={<AddIcon />}
                      sx={{direction: 'ltr'}}
                    >
                      יצור {productLabel} חדש
                    </Button>
                    
                    <Button
                      ref={addProductButtonRef}
                      onClick={handleAddProductToCurrent}
                      variant="contained"
                      color="primary"
                      disabled={!selectedProduct || (storageEnabled && noInventoryWarning) || isAddingProduct}
                    >
                      {isAddingProduct ? 'מוסיף...' : `הוסף ${productLabel} ובחר נוסף`}
                    </Button>
                  </Box>
                )}
                
                {/* Main actions section */}
                <Box sx={{ 
                  width: '100%',
                  padding: 2,
                  display: 'flex',
                  justifyContent: 'space-between'
                }}>
                  {/* Cancel on the right */}
                  <Button 
                    onClick={handleCancelProductDialog}
                    variant="contained"
                    sx={{ 
                      backgroundColor: 'error.main',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'error.dark'
                      }
                    }}
                  >
                    ביטול
                  </Button>
                  
                  {/* Main action buttons on the left */}
                  {productCreationMode ? (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Button 
                        onClick={toggleProductCreationMode} 
                        variant="contained"
                        color="secondary"
                        startIcon={<ArrowBackIcon />}
                        sx={{ direction: 'ltr' }}
                      >
                        חזרה לבחירת {productLabel}
                      </Button>
                      <Button 
                        onClick={handleCreateProduct} 
                        variant="contained" 
                        color="primary"
                        disabled={createProductMutation.isLoading || creatingProductWithInventory}
                      >
                        {(createProductMutation.isLoading || creatingProductWithInventory) ? 
                          `יוצר ${productLabel} ומלאי...` : 
                          `צור ${productLabel} והוסף להזמנה`}
                      </Button>
                    </Box>
                  ) : editingProductId !== null ? (
                    <Button 
                      onClick={handleAddProduct} 
                      variant="contained" 
                      color="primary"
                      disabled={!selectedProduct}
                    >
                      עדכן {productLabel}
                    </Button>
                  ) : (
                    <Button
                      onClick={handleFinishAddingProducts}
                      variant="contained"
                      color="success"
                      disabled={addedProducts.length === 0 || isFinishingProducts}
                    >
                      {isFinishingProducts ? 'מעדכן...' : `סיים (${addedProducts.length})`}
                    </Button>
                  )}
                </Box>
              </DialogActions>
            </MobileDialog>
          </Box>
        );
      
      case 2: // Payment details
        return (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              פרטי תשלום ו{orderLabel}
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={heLocale}>
                  <DatePicker
                    label={orderFieldsSchema.OrderDate?.label || `תאריך ${orderLabel}`}
                    value={formData.OrderDate}
                    onChange={(newDate) => setFormData(prev => ({ ...prev, OrderDate: newDate }))}
                    slotProps={{ textField: { variant: 'outlined', fullWidth: true, margin: 'normal' } }}
                    sx={{direction: 'ltr'}}
                  />
                </LocalizationProvider>
              </Grid>
              { orderFields.includes("DueDate") && 
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={heLocale}>
                  <DatePicker
                    label={orderFieldsSchema.DueDate?.label || "תאריך אספקה"}
                    value={formData.DueDate}
                    onChange={(newDate) => setFormData(prev => ({ ...prev, DueDate: newDate }))}
                    slotProps={{ textField: { variant: 'outlined', fullWidth: true, margin: 'normal' } }}
                    sx={{direction: 'ltr'}}
                  />
                </LocalizationProvider>
              </Grid>
              }

              {orderItemFields.includes("Status")  &&
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  label={orderFieldsSchema.Status?.label || "סטטוס"}
                  name="Status"
                  value={formData.Status}
                  onChange={handleInputChange}
                  variant="outlined"
                  fullWidth
                  margin="normal"
                >
                  {(orderFieldsSchema.Status?.options || [
                    { value: 'pending', label: 'בתהליך' },
                    { value: 'processing', label: 'בטיפול' },
                    { value: 'completed', label: 'הושלם' },
                    { value: 'cancelled', label: 'בוטל' },
                  ]).map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              }

              <Grid item xs={12} md={6}>
                <TextField
                  label={orderFieldsSchema.InvoiceNumber?.label || "מספר חשבונית"}
                  name="InvoiceNumber"
                  value={formData.InvoiceNumber}
                  onChange={handleInputChange}
                  variant="outlined"
                  fullWidth
                  margin="normal"
                />
              </Grid>
              
              {/* Discount section */}
              { orderFields.includes("DiscountAmount") &&
              <> 
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  הנחה
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <RadioGroup
                    row
                    name="discount-type"
                    value={discountType}
                    onChange={handleDiscountTypeChange}
                  >
                    <FormControlLabel value="none" control={<Radio />} label="ללא הנחה" />
                    <FormControlLabel value="amount" control={<Radio />} label="סכום קבוע" />
                    <FormControlLabel value="percentage" control={<Radio />} label="אחוז" />
                  </RadioGroup>
                </FormControl>
              </Grid>

              {discountType !== 'none' && (
                <Grid item xs={12} md={6}>
                  {discountType === 'amount' ? (
                    <TextField
                      label="סכום הנחה"
                      type="number"
                      value={formData.DiscountAmount}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        DiscountAmount: e.target.value // Keep as string
                      }))}
                      onFocus={(e) => e.target.select()}
                      variant="outlined"
                      fullWidth

                      sx={{
                        direction: 'ltr',
                        '& input': {
                          textAlign: 'right',
                          '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                            WebkitAppearance: 'none',
                            margin: 0,
                          },
                          '-moz-appearance': 'textfield',
                        },
                      }}
                    />
                  ) : (
                    <TextField
                      label="אחוז הנחה"
                      type="number"
                      value={formData.DiscountPercentage}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        DiscountPercentage: e.target.value  // Just store the value as-is
                      }))}
                      onFocus={(e) => e.target.select()}
                      variant="outlined"
                      fullWidth
                      InputProps={{ sx: { fontSize: '1.2rem' } }}
                      InputLabelProps={{ sx: { fontSize: '1.2rem' } }}
                      sx={{
                        direction: 'ltr',
                        '& input': {
                          textAlign: 'right',
                          '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                            WebkitAppearance: 'none',
                            margin: 0,
                          },
                          '-moz-appearance': 'textfield',
                        },
                      }}
                    />
                  )}
                </Grid>
              )}

              {discountType !== 'none' && (
                <Grid item xs={12} md={8}>
                  <TextField
                    label="סיבת הנחה"
                    value={formData.DiscountReason}
                    onChange={(e) => setFormData(prev => ({ ...prev, DiscountReason: e.target.value }))}
                    variant="outlined"
                    fullWidth
                    size="small"
                    margin="dense"
                    placeholder="פרט את סיבת ההנחה כאן"
                  />
                </Grid>
              )}
              </>
              }

              {/* Tax section */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  מע"מ
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <RadioGroup
                    row
                    name="tax-state"
                    value={taxState}
                    onChange={handleTaxStateChange}
                  >
                    <FormControlLabel value="none" control={<Radio />} label="ללא" />
                    <FormControlLabel value="included" control={<Radio />} label="כולל מע&quot;מ" />
                    <FormControlLabel value="excluded" control={<Radio />} label="חייב במע&quot;מ - לא כולל" />
                  </RadioGroup>
                </FormControl>
              </Grid>

              {taxState !== 'none' && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    שיעור מע"מ: {formData.TaxRate}%
                  </Typography>
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField
                  label="הערות"
                  name="Comments"
                  value={formData.Comments}
                  onChange={handleInputChange}
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={4}
                  margin="normal"
                />
              </Grid>
            </Grid>
          </Box>
        );
      
      case 3: // Summary
        return (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              סיכום {orderLabel}
            </Typography>
            
            <Paper sx={{ p: 3, mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle1" color="text.secondary">פרטי לקוח</Typography>
                  <Typography variant="body1">{formData.ClientName}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    מזהה לקוח: {formData.ClientBusinessID}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle1" color="text.secondary">פרטי {orderLabel}</Typography>
                  <Typography variant="body1">
                    תאריך {orderLabel}: {formData.OrderDate.toLocaleDateString('he-IL')}
                  </Typography>
                  {orderFields.includes("DueDate") && formData.DueDate && (
                    <Typography variant="body1">
                      תאריך אספקה: {formData.DueDate.toLocaleDateString('he-IL')}
                    </Typography>
                  )}
                  { orderFields.includes("Status") && 
                  <Typography variant="body1">
                    סטטוס: {(orderFieldsSchema.Status?.options || [
                      { value: 'pending', label: 'בתהליך' },
                      { value: 'processing', label: 'בטיפול' },
                      { value: 'completed', label: 'הושלם' },
                      { value: 'cancelled', label: 'בוטל' },
                    ]).find(o => o.value === formData.Status)?.label || formData.Status}
                  </Typography>
                  }
                  
                  {formData.InvoiceNumber && (
                    <Typography variant="body1">
                      מספר חשבונית: {formData.InvoiceNumber}
                    </Typography>
                  )}
                </Grid>
                
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                    {productPluralLabel} ({productEntries.length})
                  </Typography>
                  
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>שם {productLabel}</TableCell>
                          <TableCell align="center">כמות</TableCell>
                          {storageEnabled && <TableCell align="center">מחסן</TableCell>}
                          <TableCell align="right">מחיר יחידה</TableCell>
                          <TableCell align="right">סה"כ</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {productEntries.map((entry, index) => {
                          const actualPrice = entry.customPrice || entry.product.Price;
                          
                          return (
                            <TableRow key={entry.entryId || index}>
                              <TableCell>{entry.product.ProductName}</TableCell>
                              <TableCell align="center">{entry.quantity}</TableCell>
                              {storageEnabled && (
                                <TableCell align="center">
                                  {entry.storageName || 'ברירת מחדל'}
                                </TableCell>
                              )}
                              <TableCell align="right">
                                {new Intl.NumberFormat('he-IL', {
                                  style: 'currency',
                                  currency: 'ILS',
                                }).format(actualPrice)}
                              </TableCell>
                              <TableCell align="right">
                                {new Intl.NumberFormat('he-IL', {
                                  style: 'currency',
                                  currency: 'ILS',
                                }).format(actualPrice * entry.quantity)}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
                
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                    <Typography variant="subtitle1">סה"כ לפני הנחה:</Typography>
                    <Typography variant="subtitle1">
                      {new Intl.NumberFormat('he-IL', {
                        style: 'currency',
                        currency: 'ILS',
                      }).format(formData.Subtotal)}
                    </Typography>
                  </Box>
                  
                  {formData.DiscountAmount > 0 && (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="subtitle1">
                          הנחה{discountType === 'percentage' ? ` (${formData.DiscountPercentage}%)` : ''}:
                        </Typography>
                        <Typography variant="subtitle1" color="error.main">
                          -{new Intl.NumberFormat('he-IL', {
                            style: 'currency',
                            currency: 'ILS',
                          }).format(formData.DiscountAmount)}
                        </Typography>
                      </Box>

                      {/* Show net amount after discount */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                        <Typography variant="subtitle1">
                          סה"כ אחרי הנחה:
                        </Typography>
                        <Typography variant="subtitle1">
                          {formatCurrency(formData.Subtotal - formData.DiscountAmount)}
                        </Typography>
                      </Box>
                    </>
                  )}

                  {/* Tax breakdown */}
                  {taxState === 'included' && formData.TaxAmount > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        מזה מע"מ ({formData.TaxRate}%):
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatCurrency(formData.TaxAmount)}
                      </Typography>
                    </Box>
                  )}

                  {taxState === 'excluded' && formData.TaxAmount > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                      <Typography variant="subtitle1">
                        מע"מ ({formData.TaxRate}%):
                      </Typography>
                      <Typography variant="subtitle1">
                        +{formatCurrency(formData.TaxAmount)}
                      </Typography>
                    </Box>
                  )}

                  {/* Rounding section */}
                  {roundingEnabled && formData.RoundingAdjustment !== 0 && (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          סה"כ לפני עיגול:
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatCurrency(formData.TotalAmount - formData.RoundingAdjustment)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="primary.main">
                          עיגול:
                        </Typography>
                        <Typography variant="body2" color="primary.main">
                          {formatCurrency(formData.RoundingAdjustment)}
                        </Typography>
                      </Box>
                    </>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', mt: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">סה"כ לתשלום:</Typography>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {new Intl.NumberFormat('he-IL', {
                        style: 'currency',
                        currency: 'ILS',
                      }).format(formData.TotalAmount)}
                    </Typography>
                  </Box>

                  {/* Rounding checkbox */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={roundingEnabled}
                          onChange={handleRoundingChange}
                        />
                      }
                      label="עיגול"
                    />
                  </Box>
                </Grid>

                {/* Payment section */}
                { orderFields.includes("PaidAmount") &&
                <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" gutterBottom>
                      תשלומים
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<AddIcon />}
                      onClick={() => openPaymentDialog()}
                      sx={{direction: 'ltr'}}
                    >
                      הוסף תשלום
                    </Button>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="subtitle1">
                        שולם עד כה:
                      </Typography>
                      <Typography variant="subtitle1">
                        {formatCurrency(formData.PaidAmount)}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        יתרה לתשלום:
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        color={remainingBalance > 0 ? 'error.main' : 'success.main'}
                      >
                        {formatCurrency(remainingBalance)}
                      </Typography>
                    </Box>
                  </Paper>

                  {formData.payments.length > 0 ? (
                    <TableContainer component={Paper} sx={{ mb: 3 }}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>אמצעי תשלום</TableCell>
                            <TableCell>פרטים</TableCell>
                            <TableCell align="right">סכום</TableCell>
                            <TableCell align="center">פעולות</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {formData.payments.map((payment, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {getPaymentMethodIcon(payment.method)}
                                  <Typography>{getPaymentMethodName(payment.method)}</Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {getPaymentDetails(payment) || '—'}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(payment.amount)}
                              </TableCell>
                              <TableCell align="center">
                                <Tooltip title="ערוך">
                                  <IconButton
                                    color="primary"
                                    size="small"
                                    onClick={() => openPaymentDialog(index)}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="הסר">
                                  <IconButton
                                    color="error"
                                    size="small"
                                    onClick={() => handleDeletePayment(index)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Alert severity="info" sx={{ mb: 3 }}>
                      אין תשלומים {orderLabel} זו. ניתן להוסיף תשלום באמצעות כפתור "הוסף תשלום".
                    </Alert>
                  )}
                </Grid>
                </>
                }

                {formData.Comments && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                      הערות
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                      <Typography variant="body2">{formData.Comments}</Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Box>
        );
      
      default:
        return null;
    }
  };
  
  // Step icons
  const getStepIcon = (index) => {
    switch (index) {
      case 0:
        return <PersonIcon />;
      case 1:
        return <ShoppingCartIcon />;
      case 2:
        return <PaymentIcon />;
      case 3:
        return <CheckCircleIcon />;
      default:
        return null;
    }
  };
  
  // Helper function to get step status text for screen readers
  const getStepStatus = (index) => {
    if (index === activeStep) return "שלב נוכחי";
    if (index < activeStep) return "שלב הושלם";
    return "שלב עתידי";
  };
    
  const InventoryWarningAlert = ({ product, onOpenProductPage }) => {
    if (!product) return null;
    
    return (
      <Alert 
        severity="warning" 
        icon={<WarningIcon />}
        sx={{ mt: 2, mb: 2 }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
            אין מלאי זמין עבור {productLabel} זה
          </Typography>
          <Typography variant="body2">
            לא ניתן להוסיף {productLabel} זה להזמנה מכיוון שאין מלאי זמין באף מחסן.
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<OpenInNewIcon />}
              onClick={onOpenProductPage}
              sx={{ direction: 'ltr' }}
            >
              עדכון מלאי
            </Button>
            <Typography 
              variant="body1" 
              sx={{ 
                fontWeight: 'bold',
                fontSize: '1.1rem',
                color: 'warning.dark',
                backgroundColor: 'warning.lighter',
                px: 2,
                py: 0.5,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'warning.main'
              }}
            >
              אחרי העדכון, בחר מחדש את ה{productLabel}
            </Typography>
          </Box>
        </Box>
      </Alert>
    );
  };
  return (
    <ThemeProvider theme={theme}>
      <div dir="rtl">
        <Container maxWidth="lg" sx={{ my: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h4" component="h1">
                {newOrderLabel}
              </Typography>
              <Button
                size="small"
                variant="text"
                onClick={() => navigate('/order/new' + (location.search || ''))}
                sx={{ direction: 'ltr' }}
              >
                נסה את הדף החדש
              </Button>
            </Box>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/orders')}
              sx={{direction: 'ltr'}}
            >
              חזרה לרשימה
            </Button>
          </Box>
          
          {(isErrorClients || isErrorProducts) && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {isErrorClients && 'שגיאה בטעינת רשימת הלקוחות. '}
              {isErrorProducts && `שגיאה בטעינת רשימת ${productPluralLabel}. `}
              אנא רענן את הדף ונסה שוב.
            </Alert>
          )}
          
          {formError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {formError}
            </Alert>
          )}
          
          {formSuccess && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {formSuccess}
            </Alert>
          )}
          
          <Card sx={{ borderRadius: 2, boxShadow: 3, mb: 4 }}>
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography sx={{ ml: 4, color: 'common.white' }}>
                    שלב {activeStep + 1} מתוך {steps.length}: {steps[activeStep]}
                  </Typography>
                </Box>
              }
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
              }}
            />
            <CardContent>
              <Stepper 
                activeStep={activeStep} 
                alternativeLabel 
                sx={{ mb: 4 }}
                connector={<NoConnector />}
              >
                {steps.map((label, index) => {
                  const isActive = index === activeStep;
                  const isCompleted = index < activeStep;
                  return (
                    <Step key={label}>
                      <StepLabel 
                        StepIconComponent={() => (
                          <Box 
                            sx={{ 
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 36,
                              height: 36,
                              borderRadius: '50%',
                              bgcolor: isActive ? 'primary.main' : (isCompleted ? 'success.light' : 'grey.300'),
                              color: isActive || isCompleted ? 'white' : 'text.secondary',
                              opacity: index > activeStep ? 0.7 : 1
                            }}
                            aria-label={getStepStatus(index)}
                          >
                            {getStepIcon(index)}
                          </Box>
                        )}
                      >
                        <Typography 
                          sx={{ 
                            fontWeight: isActive ? 'bold' : (isCompleted ? 600 : 400),
                            opacity: index > activeStep ? 0.6 : 1,
                            color: isActive ? 'primary.main' : (isCompleted ? 'text.primary' : 'text.secondary')
                          }}
                        >
                          {label}
                        </Typography>
                      </StepLabel>
                    </Step>
                  );
                })}
              </Stepper>
              
              {/* Form content based on active step */}
              {renderStepContent(activeStep)}
              
              {/* Navigation buttons */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  disabled={activeStep === 0 || createOrderMutation.isLoading || isSubmitting}
                >
                  חזור
                </Button>
                <Box>
                  {activeStep === steps.length - 1 ? (
                    <Button 
                      variant="contained" 
                      color="primary"
                      onClick={handleSubmit}
                      disabled={createOrderMutation.isLoading || isSubmitting}
                      startIcon={<SaveIcon />}
                      sx={{direction: 'ltr'}}
                    >
                      {(createOrderMutation.isLoading || isSubmitting) ? `יוצר ${orderLabel}...` : `יצירת ${orderLabel}`}
                    </Button>
                  ) : (
                    <Button 
                      variant="contained" 
                      onClick={handleNext}
                    >
                      המשך
                    </Button>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Container>
        
        {/* Client Create Dialog */}
        {createClientDialogOpen && (
          <ClientCreateDialog
            open={createClientDialogOpen}
            onClose={handleCloseCreateClientDialog}
            userProfile={userProfile}
          />
        )}

        {/* Add/Edit Payment Dialog */}
        <MobileDialog
          open={addPaymentOpen}
          onClose={() => setAddPaymentOpen(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            {editingPaymentIndex !== null ? 'עריכת תשלום' : 'הוספת תשלום'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1 }}>
              {formError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {formError}
                </Alert>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, alignItems: 'center' }}>
                <Typography variant="subtitle2">
                  יתרה לתשלום:
                </Typography>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  color={remainingBalance > 0 ? 'error.main' : 'success.main'}
                >
                  {formatCurrency(remainingBalance)}
                </Typography>
              </Box>

              <Button
                variant="outlined"
                size="small"
                onClick={() => setPaymentAmount(remainingBalance.toString())}
                sx={{ mb: 1 }}
              >
                השתמש ביתרה מלאה
              </Button>

              <TextField
                label="סכום"
                type="number"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                onFocus={(e) => e.target.select()}
                fullWidth
                margin="normal"
                autoFocus
                InputProps={{
                  startAdornment: <InputAdornment position="start">₪</InputAdornment>
                }}
                sx={{
                    direction: 'ltr',
                    '& input': {
                      textAlign: 'right',
                      '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                        WebkitAppearance: 'none',
                        margin: 0,
                      },
                      '-moz-appearance': 'textfield',
                    },
                  }}
                required
              />

              <FormControl fullWidth margin="normal">
                <InputLabel>אמצעי תשלום</InputLabel>
                <Select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  label="אמצעי תשלום"
                >
                  <MenuItem value="cash">מזומן</MenuItem>
                  <MenuItem value="credit_card">כרטיס אשראי</MenuItem>
                  <MenuItem value="bank_transfer">העברה בנקאית</MenuItem>
                  <MenuItem value="check">צ'ק</MenuItem>
                  <MenuItem value="other">אחר</MenuItem>
                </Select>
              </FormControl>

              {/* Payment method specific fields */}
              {paymentMethod === 'check' && (
                <>
                  <TextField
                    label="מספר צ'ק"
                    value={paymentMethodFields.checkNumber}
                    onChange={(e) => setPaymentMethodFields(prev => ({ ...prev, checkNumber: e.target.value }))}
                    fullWidth
                    margin="normal"
                  />

                  <TextField
                    label="בנק"
                    value={paymentMethodFields.checkBankName}
                    onChange={(e) => setPaymentMethodFields(prev => ({ ...prev, checkBankName: e.target.value }))}
                    fullWidth
                    margin="normal"
                  />

                  <TextField
                    label="מספר חשבון"
                    value={paymentMethodFields.checkAccountNumber}
                    onChange={(e) => setPaymentMethodFields(prev => ({ ...prev, checkAccountNumber: e.target.value }))}
                    fullWidth
                    margin="normal"
                  />

                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={heLocale}>
                    <DatePicker
                      label="תאריך צ'ק"
                      value={paymentMethodFields.checkDate}
                      onChange={(newDate) => setPaymentMethodFields(prev => ({ ...prev, checkDate: newDate }))}
                      slotProps={{ textField: { fullWidth: true, margin: 'normal' } }}
                      sx={{direction: 'ltr'}}
                    />
                  </LocalizationProvider>
                </>
              )}

              {paymentMethod === 'credit_card' && (
                <>
                  <TextField
                    label="4 ספרות אחרונות"
                    value={paymentMethodFields.cardLastFour}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                      setPaymentMethodFields(prev => ({ ...prev, cardLastFour: value }));
                    }}
                    fullWidth
                    margin="normal"
                    inputProps={{ maxLength: 4 }}
                  />

                  <FormControl fullWidth margin="normal">
                    <InputLabel>סוג כרטיס</InputLabel>
                    <Select
                      value={paymentMethodFields.cardType}
                      onChange={(e) => setPaymentMethodFields(prev => ({ ...prev, cardType: e.target.value }))}
                      label="סוג כרטיס"
                    >
                      <MenuItem value="">בחר סוג כרטיס</MenuItem>
                      <MenuItem value="Visa">Visa</MenuItem>
                      <MenuItem value="Mastercard">Mastercard</MenuItem>
                      <MenuItem value="American Express">American Express</MenuItem>
                      <MenuItem value="Diners">Diners</MenuItem>
                      <MenuItem value="IsraCard">ישראכרט</MenuItem>
                    </Select>
                  </FormControl>
                </>
              )}

              {paymentMethod === 'bank_transfer' && (
                <>
                  <TextField
                    label="שם הבנק"
                    value={paymentMethodFields.bankName}
                    onChange={(e) => setPaymentMethodFields(prev => ({ ...prev, bankName: e.target.value }))}
                    fullWidth
                    margin="normal"
                  />

                  <TextField
                    label="4 ספרות אחרונות של החשבון"
                    value={paymentMethodFields.bankAccountLast4}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                      setPaymentMethodFields(prev => ({ ...prev, bankAccountLast4: value }));
                    }}
                    fullWidth
                    margin="normal"
                    inputProps={{ maxLength: 4 }}
                  />

                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={heLocale}>
                    <DatePicker
                      label="תאריך העברה"
                      value={paymentMethodFields.transferDate}
                      onChange={(newDate) => setPaymentMethodFields(prev => ({ ...prev, transferDate: newDate }))}
                      slotProps={{ textField: { fullWidth: true, margin: 'normal' } }}
                      sx={{direction: 'ltr'}}
                    />
                  </LocalizationProvider>
                </>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddPaymentOpen(false)}>ביטול</Button>
            <Button
              onClick={handleAddPayment}
              variant="contained"
              color="primary"
            >
              {editingPaymentIndex !== null ? 'עדכן תשלום' : 'הוסף תשלום'}
            </Button>
          </DialogActions>
        </MobileDialog>

        {/* Draft restore dialog */}
        <MobileDialog
          open={draftDialogOpen}
          onClose={() => {}}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>טיוטת {orderLabel}</DialogTitle>
          <DialogContent>
            <Typography>
              נמצאה טיוטת {orderLabel} שלא הושלמה. האם להמשיך לערוך אותה או ליצור {orderLabel} חדשה?
            </Typography>
            {pendingDraft?.selectedClient && (
              <Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {pendingDraft.selectedClient.ClientName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {pendingDraft.selectedClient.ClientBusinessID}
                </Typography>
                {pendingDraft.productEntries?.length > 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {pendingDraft.productEntries.length} {productPluralLabel}
                  </Typography>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDiscardDraft}>{orderLabel} חדשה</Button>
            <Button variant="contained" onClick={handleRestoreDraft}>המשך עריכה</Button>
          </DialogActions>
        </MobileDialog>
      </div>
    </ThemeProvider>
  );
};

export default NewOrderPageLegacy;